import React, { useEffect, useState, useRef } from "react";



import AOS from "aos";
import "aos/dist/aos.css";
import service from "../../Service/Service";
import PriceDisplay from "../PriceDisplay";
import { convertToGBP, formatCurrency } from "../../utils/currencyConverter";

import BannerSection from "../Home3/BannerSection";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowDown, Loader } from "react-feather";
import { ArrowRight, ArrowRightLeft, ArrowUp, Briefcase, ChevronDown, ChevronUp, Luggage, Pencil, Phone, PhoneCall, Rocket, Clock, MapPin, Plane, X } from "lucide-react";
import parse from 'html-react-parser';
import InputSection from "../Home3/InputSection";
import loadersvg from "../../assets/images/loader.svg";
import whatsapp from "../../assets/images/icons/whatsapp.png";
import arrowsLeftRight from "../../assets/images/arrowsleftright.png";
import Navbar from "../navbar";
import Button from "../Button/Button";


function extractTime(timeString) {
    const regex = /(\d+)(H)?(\d+)(M)?/;
    const matches = timeString.match(regex);

    let hours = 0;
    let minutes = 0;

    if (matches) {
        // Extract hours if present
        if (matches[2] === 'H') {
            hours = parseInt(matches[1]);
        }
        // Extract minutes if present
        if (matches[4] === 'M') {
            minutes = parseInt(matches[3]);
        }
    }

    return { hours, minutes };
}

// Extract dep/arr datetime from a segment (Duffel or Amadeus format) for card display
function getSegmentDepTime(seg) {
    return seg?.departing_at || seg?.origin?.datetime || seg?.origin?.time || seg?.departure?.at || seg?.departure?.datetime;
}
function getSegmentArrTime(seg) {
    return seg?.arriving_at || seg?.destination?.datetime || seg?.destination?.time || seg?.arrival?.at || seg?.arrival?.datetime;
}

// Helper function to fetch and match Duffel return offers to outbound offers
async function fetchAndMatchDuffelReturnOffers(outboundOffers, bookingParams) {
    if (!bookingParams?.return_date || !bookingParams?.arrival || !bookingParams?.departure) {
        return new Map(); // Return empty map if no return date
    }
    
    try {
        const returnResponse = await service.postGetFlightOfferDuffel({
            arrival: bookingParams.departure, // Return: destination -> origin
            departure: bookingParams.arrival,  // Return: origin -> destination
            date: bookingParams.return_date,
            return_date: '', // One-way return search
            adults: bookingParams.adults || '1',
            children: bookingParams.children || '0',
            infants: bookingParams.infants || '0',
            flight_type: 'one-way',
            flight_class: bookingParams.flight_class || 'economy'
        });
        
        const returnOffers = returnResponse.data?.oneway?.offers || [];
        
        // Group return offers by airline (normalize airline names for matching)
        const returnOffersByAirline = new Map();
        returnOffers.forEach(offer => {
            const airline = offer.owner?.name;
            if (airline) {
                const normalizedAirline = airline.trim().toLowerCase();
                if (!returnOffersByAirline.has(normalizedAirline)) {
                    returnOffersByAirline.set(normalizedAirline, []);
                }
                returnOffersByAirline.get(normalizedAirline).push(offer);
            }
        });
        
        // For each airline, sort by price and keep cheapest
        const cheapestReturnByAirline = new Map();
        returnOffersByAirline.forEach((offers, normalizedAirline) => {
            const sorted = offers.sort((a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount));
            cheapestReturnByAirline.set(normalizedAirline, sorted[0]);
        });
        
        return cheapestReturnByAirline;
    } catch (error) {
        console.error('Error fetching Duffel return offers:', error);
        return new Map(); // Return empty map on error
    }
}
function mapDuffelSegments(rawSegs) {
    return (rawSegs || []).map(seg => ({
        ...seg,
        departing_at: getSegmentDepTime(seg) || seg.departing_at,
        arriving_at: getSegmentArrTime(seg) || seg.arriving_at,
    }));
}

// Helper function to get airline logo from carrier code
function getAirlineLogo(carrierCode) {
    if (!carrierCode) return '';
    
    // Using multiple CDN sources as fallback
    // Primary: Google Flights
    return `https://www.gstatic.com/flights/airline_logos/70px/${carrierCode}.png`;
    
    // Alternative sources (can be used as fallbacks):
    // return `https://images.kiwi.com/airlines/64/${carrierCode}.png`;
    // return `https://content.airhex.com/content/logos/airlines_${carrierCode}_50_50_s.png`;
}

// Flight price in GBP (used for all flight cards: Duffel, Amadeus)
function FlightPriceGbp({ amount, sourceCurrency = 'GBP', className = '' }) {
    const [gbpAmount, setGbpAmount] = useState(null);
    useEffect(() => {
        if (amount == null || amount === '' || isNaN(amount)) {
            setGbpAmount(null);
            return;
        }
        let cancelled = false;
        convertToGBP(amount, sourceCurrency === 'USD' ? 'USD' : 'GBP').then((val) => {
            if (!cancelled) setGbpAmount(val);
        });
        return () => { cancelled = true; };
    }, [amount, sourceCurrency]);
    if (amount == null || amount === '' || isNaN(amount)) return <span className={className}>-</span>;
    if (gbpAmount == null) return <span className={className}>£...</span>;
    return <span className={className}><PriceDisplay amount={gbpAmount} displayCurrency="GBP" /></span>;
}

// Total of multiple flight prices in GBP (for selected slice strip)
function TotalFlightPriceGbp({ flights = [], className = '' }) {
    const [totalGbp, setTotalGbp] = useState(null);
    useEffect(() => {
        if (!flights.length) {
            setTotalGbp(0);
            return;
        }
        let cancelled = false;
        Promise.all(flights.map((f) => convertToGBP(f.amount, f.currency === 'USD' ? 'USD' : 'GBP')))
            .then((vals) => (cancelled ? undefined : setTotalGbp(vals.reduce((a, b) => a + b, 0))));
        return () => { cancelled = true; };
    }, [flights]);
    if (flights.length === 0) return <span className={className}>-</span>;
    if (totalGbp == null) return <span className={className}>£...</span>;
    return <span className={className}><PriceDisplay amount={totalGbp} displayCurrency="GBP" /></span>;
}

// Helper function to get all unique airlines from flight segments
function getAllAirlinesFromFlight(flight) {
    const airlines = new Map();
    
    // For Amadeus flights
    if (flight.rawAmadeusOffer) {
        const itineraries = flight.rawAmadeusOffer.itineraries || [];
        itineraries.forEach(itinerary => {
            const segments = itinerary.segments || [];
            segments.forEach(segment => {
                const carrierCode = segment.carrierCode;
                const carrierName = segment.operating?.carrierCode || carrierCode;
                if (carrierCode && !airlines.has(carrierCode)) {
                    airlines.set(carrierCode, {
                        code: carrierCode,
                        name: carrierName,
                        logo: getAirlineLogo(carrierCode)
                    });
                }
            });
        });
    }
    
    // For Duffel flights
    if (flight.slices) {
        flight.slices.forEach(slice => {
            const segments = slice.segments || [];
            segments.forEach(segment => {
                const carrierCode = segment.operating_carrier?.iata_code || segment.marketing_carrier?.iata_code;
                const carrierName = segment.operating_carrier?.name || segment.marketing_carrier?.name;
                if (carrierCode && !airlines.has(carrierCode)) {
                    airlines.set(carrierCode, {
                        code: carrierCode,
                        name: carrierName,
                        logo: getAirlineLogo(carrierCode)
                    });
                }
            });
        });
    }
    
    // For FlightLabs flights (single segment; no IATA for carrier, use name as key)
    if (flight.rawFlightLabsOffer) {
        const name = flight.rawFlightLabsOffer.marketingCarrier || flight.rawFlightLabsOffer.operatingCarrier || 'FlightLabs';
        const code = (flight.rawFlightLabsOffer.marketingCarrier || '').replace(/\s*\([^)]*\)\s*/g, '').slice(0, 2).toUpperCase() || 'FL';
        if (name && !airlines.has(code)) {
            airlines.set(code, {
                code,
                name,
                logo: getAirlineLogo(code)
            });
        }
    }
    
    return Array.from(airlines.values());
}

const FlightDetailNewDuffel = (props) => {
    const [flights, setFlights] = useState([]);
    const [flightsOrg, setFlightsOrg] = useState([]);
    const [packagee, setPackage] = useState();
    const [packageData, setPackageData] = useState();
    const [loading, setLoading] = useState(false);
    const [paras, setParas] = useState([]);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [toast, showToast] = useState(false)
    const [error, setError] = useState('')
    const [refresh, setRefresh] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [selectedPackage, setSelectedPackage] = useState()
    const [bookinginfo, setBookinginfo] = useState()
    const [selectedSlice, setSelectedSlice] = useState([])
    const [selectedSort, setSelectedSort] = useState('least-exp')
    const [selectedAirlines, setSelectedAirlines] = useState([])
    const [airlines, setAirlines] = useState([])
    const [flightNo, setFlightNo] = useState()
    const [offers, setOffers] = useState([])
    const [payment_type, setPaymentType] = useState('full-payment')
    const [firstSort, setFirstSort] = useState(false)
    const [showDiscountDialog, setShowDiscountDialog] = useState(false)
    const [dialogName, setDialogName] = useState('')
    const [dialogPhone, setDialogPhone] = useState('')
    const [dialogError, setDialogError] = useState('')
    const [outsideClickCount, setOutsideClickCount] = useState(0)
    const [flightAlternatives, setFlightAlternatives] = useState({}) // Store alternative fare options
    const [selectedFlightForBooking, setSelectedFlightForBooking] = useState(null) // Track which flight is being booked
    const [showAlternativesModal, setShowAlternativesModal] = useState(false) // Show alternatives modal
    const [airlineStripVisible, setAirlineStripVisible] = useState(true) // Swipe up = hide, swipe down = show
    const [showChangeFlightOverlay, setShowChangeFlightOverlay] = useState(false)
    const [visibleFlightsCount, setVisibleFlightsCount] = useState(20)
    const [loadingMore, setLoadingMore] = useState(false)
    const [showMobileBookingOverlay, setShowMobileBookingOverlay] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const lastScrollYRef = useRef(0)
    const loadMoreSentinelRef = useRef(null)
    const flightsScrollContainerRef = useRef(null)
    const mobileScrollYRef = useRef(0)
    const mobileOriginalStylesRef = useRef({})
    const alternativesScrollYRef = useRef(0)
    const alternativesOriginalStylesRef = useRef({})
    const isRestoringScrollRef = useRef(false) // Track when we're restoring scroll position
    const searchInProgressRef = useRef(false) // Prevent duplicate API calls (e.g. React Strict Mode double-mount)
    const navigate = useNavigate()
    
    // Progressive loading state - start with loading true to show immediately
    const [loadingProgress, setLoadingProgress] = useState({
        isLoading: true,
        resultsCount: 0,
        progressPercent: 0,
        startTime: Date.now()
    })

    const [searchParams, setSearchParams] = useSearchParams();

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => {
            window.removeEventListener('resize', checkMobile);
            // Cleanup: restore body overflow if overlay was open
            if (showMobileBookingOverlay) {
                document.body.style.overflow = '';
            }
        };
    }, [showMobileBookingOverlay]);

    // Handle body scroll lock when mobile overlay is open
    useEffect(() => {
        if (showMobileBookingOverlay && isMobile) {
            // Ensure scroll lock persists (already set in openBookingModal, but ensure it persists)
            const scrollY = mobileScrollYRef.current;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            
            // Push history state when opening overlay
            window.history.pushState({ overlayOpen: 'booking' }, '');
            
            // Keep airline strip visible when overlay is open
            setAirlineStripVisible(true);
        }
        
        return () => {
            // Cleanup on unmount - restoration is handled in closeBookingModal
        };
    }, [showMobileBookingOverlay, isMobile]);

    // Keep airline strip visible when alternatives modal is open or loading
    useEffect(() => {
        if (showAlternativesModal || (loading && selectedFlightForBooking)) {
            setAirlineStripVisible(true);
        }
    }, [showAlternativesModal, loading, selectedFlightForBooking]);

    // Handle back button to close overlays
    useEffect(() => {
        const handlePopState = (event) => {
            // Check if we have an overlay open by checking the state
            const state = event.state;
            
            // If booking overlay is open, close it instead of navigating
            if (showMobileBookingOverlay || state?.overlayOpen === 'booking') {
                closeBookingModal();
                // Prevent default navigation by staying on current page
                window.history.pushState(null, '', window.location.href);
                return;
            }
            
            // If alternatives modal is open, close it instead of navigating
            if (showAlternativesModal || state?.overlayOpen === 'alternatives') {
                closeAlternativesModal();
                // Prevent default navigation by staying on current page
                window.history.pushState(null, '', window.location.href);
                return;
            }
        };

        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showMobileBookingOverlay, showAlternativesModal]);

    // Function to open booking modal (handles both desktop modal and mobile overlay)
    const openBookingModal = () => {
        // Hide loading overlay first
        setLoadingProgress(prev => ({
            ...prev,
            isLoading: false,
            progressPercent: 0,
        }));
        
        // Check mobile again at the time of opening (in case window was resized)
        const checkMobileNow = window.innerWidth <= 768;
        
        if (checkMobileNow) {
            console.log('Opening mobile booking overlay');
            // Preserve scroll position BEFORE doing anything else
            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            mobileScrollYRef.current = scrollY;
            
            // Store original styles
            mobileOriginalStylesRef.current = {
                position: document.body.style.position,
                top: document.body.style.top,
                width: document.body.style.width,
                overflow: document.body.style.overflow,
                htmlOverflow: document.documentElement.style.overflow
            };
            
            // Lock scroll position IMMEDIATELY using fixed positioning BEFORE opening overlay
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            
            // Override window.scrollTo temporarily to prevent any scroll
            const originalScrollTo = window.scrollTo.bind(window);
            window.scrollTo = function(...args) {
                // Only allow scroll to the saved position
                if (args.length >= 2 && args[0] === 0 && args[1] === scrollY) {
                    originalScrollTo(0, scrollY);
                    return;
                }
                // Block all other scroll attempts
                originalScrollTo(0, scrollY);
            };
            
            // Store original scrollTo for restoration
            mobileOriginalStylesRef.current.originalScrollTo = originalScrollTo;
            
            // Aggressively prevent ALL scroll events
            const preventAllScroll = (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            };
            
            // Add scroll prevention BEFORE opening overlay
            const scrollOptions = { passive: false, capture: true };
            window.addEventListener('scroll', preventAllScroll, scrollOptions);
            document.addEventListener('scroll', preventAllScroll, scrollOptions);
            window.addEventListener('wheel', preventAllScroll, scrollOptions);
            window.addEventListener('touchmove', preventAllScroll, scrollOptions);
            
            // Store listeners for cleanup
            mobileOriginalStylesRef.current.scrollListeners = { preventAllScroll, scrollOptions };
            
            // Continuously restore scroll for a short period after opening
            let restoreCount = 0;
            const maxRestores = 100; // 1000ms at 10ms intervals
            const restoreInterval = setInterval(() => {
                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                if (currentScroll !== scrollY) {
                    window.scrollTo(0, scrollY);
                    document.documentElement.scrollTop = scrollY;
                }
                restoreCount++;
                if (restoreCount >= maxRestores) {
                    clearInterval(restoreInterval);
                }
            }, 10);
            
            // Now open the overlay
            setShowMobileBookingOverlay(true);
        } else {
            console.log('Opening desktop booking modal');
            // Preserve scroll position BEFORE doing anything else
            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            const modal = document.getElementById('my_modal_flightdetail');
            if (modal) {
                // Store scroll position and original styles on the modal element itself
                modal.dataset.scrollY = scrollY.toString();
                modal.dataset.originalBodyOverflow = document.body.style.overflow;
                modal.dataset.originalHtmlOverflow = document.documentElement.style.overflow;
                modal.dataset.originalBodyPosition = document.body.style.position;
                modal.dataset.originalBodyTop = document.body.style.top;
                modal.dataset.originalBodyWidth = document.body.style.width;
                
                // Lock scroll position IMMEDIATELY using fixed positioning BEFORE opening modal
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.width = '100%';
                document.body.style.overflow = 'hidden';
                document.documentElement.style.overflow = 'hidden';
                
                // Override window.scrollTo temporarily to prevent any scroll
                const originalScrollTo = window.scrollTo.bind(window);
                modal.dataset.originalScrollTo = 'stored'; // Marker that we overrode it
                
                window.scrollTo = function(...args) {
                    // Only allow scroll to the saved position
                    if (args.length >= 2 && args[0] === 0 && args[1] === scrollY) {
                        originalScrollTo(0, scrollY);
                        return;
                    }
                    // Block all other scroll attempts
                    originalScrollTo(0, scrollY);
                };
                
                // Prevent background page scroll but allow scrolling inside the modal box
                const preventAllScroll = (e) => {
                    const modalBox = document.querySelector('#my_modal_flightdetail .modal-box');
                    if (modalBox && modalBox.contains(e.target)) return;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    return false;
                };
                
                // Add scroll prevention BEFORE opening modal
                const scrollOptions = { passive: false, capture: true };
                window.addEventListener('scroll', preventAllScroll, scrollOptions);
                document.addEventListener('scroll', preventAllScroll, scrollOptions);
                window.addEventListener('wheel', preventAllScroll, scrollOptions);
                window.addEventListener('touchmove', preventAllScroll, scrollOptions);
                
                // Add close event listener to restore scroll when modal closes
                const handleModalClose = () => {
                    const savedScrollY = parseInt(modal.dataset.scrollY || '0', 10);
                    const originalBodyOverflow = modal.dataset.originalBodyOverflow || '';
                    const originalHtmlOverflow = modal.dataset.originalHtmlOverflow || '';
                    const originalBodyPosition = modal.dataset.originalBodyPosition || '';
                    const originalBodyTop = modal.dataset.originalBodyTop || '';
                    const originalBodyWidth = modal.dataset.originalBodyWidth || '';
                    
                    // Restore original scrollTo function
                    if (modal.dataset.originalScrollTo) {
                        window.scrollTo = originalScrollTo;
                        delete modal.dataset.originalScrollTo;
                    }
                    
                    // Remove scroll prevention
                    window.removeEventListener('scroll', preventAllScroll, scrollOptions);
                    document.removeEventListener('scroll', preventAllScroll, scrollOptions);
                    window.removeEventListener('wheel', preventAllScroll, scrollOptions);
                    window.removeEventListener('touchmove', preventAllScroll, scrollOptions);
                    
                    // Restore body styles
                    document.body.style.position = originalBodyPosition;
                    document.body.style.top = originalBodyTop;
                    document.body.style.width = originalBodyWidth;
                    document.body.style.overflow = originalBodyOverflow;
                    document.documentElement.style.overflow = originalHtmlOverflow;
                    
                    // Restore scroll position
                    requestAnimationFrame(() => {
                        originalScrollTo(0, savedScrollY);
                    });
                    
                    // Clean up data attributes
                    delete modal.dataset.scrollY;
                    delete modal.dataset.originalBodyOverflow;
                    delete modal.dataset.originalHtmlOverflow;
                    delete modal.dataset.originalBodyPosition;
                    delete modal.dataset.originalBodyTop;
                    delete modal.dataset.originalBodyWidth;
                };
                
                modal.addEventListener('close', handleModalClose, { once: true });
                
                // Open modal - scroll prevention is already in place
                modal.showModal();
                
                // Continuously restore scroll for a short period after opening
                let restoreCount = 0;
                const maxRestores = 20; // 200ms at 10ms intervals
                const restoreInterval = setInterval(() => {
                    if (window.scrollY !== scrollY) {
                        window.scrollTo(0, scrollY);
                    }
                    restoreCount++;
                    if (restoreCount >= maxRestores) {
                        clearInterval(restoreInterval);
                    }
                }, 10);
            }
        }
    };

    // Function to close alternatives modal
    const closeAlternativesModal = () => {
        const scrollY = alternativesScrollYRef.current;
        const original = alternativesOriginalStylesRef.current;
        
        // Restore scroll only if we have stored styles (mobile)
        if (original.position !== undefined) {
            // Mark that we're restoring scroll
            isRestoringScrollRef.current = true;
            
            // Close modal first
            setShowAlternativesModal(false);
            
            // Restore body styles AFTER closing modal
            requestAnimationFrame(() => {
                document.body.style.position = original.position || '';
                document.body.style.top = original.top || '';
                document.body.style.width = original.width || '';
                document.body.style.overflow = original.overflow || '';
                document.documentElement.style.overflow = original.htmlOverflow || '';
                
                // Wait for browser to reflow, then restore scroll position
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo(0, scrollY);
                        document.documentElement.scrollTop = scrollY;
                        // Double-check after a brief delay
                        setTimeout(() => {
                            const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                            if (currentScroll !== scrollY) {
                                window.scrollTo(0, scrollY);
                                document.documentElement.scrollTop = scrollY;
                            }
                            // Clear restoration flag after scroll is restored
                            setTimeout(() => {
                                isRestoringScrollRef.current = false;
                            }, 200);
                        }, 50);
                    });
                });
            });
            
            // Clear refs
            alternativesOriginalStylesRef.current = {};
        } else {
            // Desktop modal - just close it
            setShowAlternativesModal(false);
        }
    };

    // Function to close booking modal
    const closeBookingModal = () => {
        if (isMobile) {
            // Get scroll position before any changes
            const scrollY = mobileScrollYRef.current;
            const original = mobileOriginalStylesRef.current;
            
            // Mark that we're restoring scroll
            isRestoringScrollRef.current = true;
            
            // Restore original scrollTo function first
            if (original.originalScrollTo) {
                window.scrollTo = original.originalScrollTo;
            }
            
            // Remove scroll prevention listeners
            if (original.scrollListeners) {
                const { preventAllScroll, scrollOptions } = original.scrollListeners;
                window.removeEventListener('scroll', preventAllScroll, scrollOptions);
                document.removeEventListener('scroll', preventAllScroll, scrollOptions);
                window.removeEventListener('wheel', preventAllScroll, scrollOptions);
                window.removeEventListener('touchmove', preventAllScroll, scrollOptions);
            }
            
            // Close overlay first
            setShowMobileBookingOverlay(false);
            
            // Restore body styles AFTER closing overlay
            requestAnimationFrame(() => {
                document.body.style.position = original.position || '';
                document.body.style.top = original.top || '';
                document.body.style.width = original.width || '';
                document.body.style.overflow = original.overflow || '';
                document.documentElement.style.overflow = original.htmlOverflow || '';
                
                // Wait for browser to reflow, then restore scroll position
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo(0, scrollY);
                        document.documentElement.scrollTop = scrollY;
                        // Double-check after a brief delay
                        setTimeout(() => {
                            const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                            if (currentScroll !== scrollY) {
                                window.scrollTo(0, scrollY);
                                document.documentElement.scrollTop = scrollY;
                            }
                            // Clear restoration flag after scroll is restored
                            setTimeout(() => {
                                isRestoringScrollRef.current = false;
                            }, 200);
                        }, 50);
                    });
                });
            });
            
            // Clear refs
            mobileOriginalStylesRef.current = {};
        } else {
            document.getElementById('my_modal_flightdetail')?.close();
        }
        // Ensure the progressive loading overlay is hidden when closing
        setLoadingProgress(prev => ({
            ...prev,
            isLoading: false,
            progressPercent: 0,
        }));
    };

    // Summary values for modal (total passengers and total price)
    const adultsCount = parseInt(bookinginfo?.adults || '0');
    const childrenCount = parseInt(bookinginfo?.children || '0');
    const infantsCount = parseInt(bookinginfo?.infants || '0');
    const totalPassengers = (adultsCount || 0) + (childrenCount || 0) + (infantsCount || 0);

    const totalSelectedAmount = selectedSlice.reduce((sum, flight) => {
        const amt = parseFloat(flight.amount || 0);
        return sum + (isNaN(amt) ? 0 : amt);
    }, 0);

    // Phone number validation — UK mobile only: 07XXXXXXXXX (11 digits)
    const isValidPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return false;
        const digits = phoneNumber.replace(/\s/g, '');
        return /^07\d{9}$/.test(digits);
    };

    const isValidEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((em || '').trim());

    // Handle phone number input - digits only, max 11 chars
    const handleDialogPhoneChange = (e) => {
        const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
        setDialogPhone(digits);
    };

    // Handler for main booking form phone inputs
    const handleMainPhoneChange = (e) => {
        const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
        setPhone(digits);
    };

    // Check if we need to show the discount dialog
    useEffect(() => {
        // Show dialog when flights are loaded and name/phone are missing
        if (!loading && flights.length > 0) {
            // Check if user has already provided details (don't show dialog again)
            const userDetailsProvided = localStorage.getItem('userDetailsProvided');
            console.log('Checking userDetailsProvided flag:', userDetailsProvided, 'Type:', typeof userDetailsProvided);
            
            // If flag is set to 'true', don't show dialog regardless of booking data
            if (userDetailsProvided === 'true') {
                console.log('User details already provided, skipping dialog');
                return;
            }
            
            // Only check booking data if flag is not set
            // Check if name and phone are missing
            const bookingData = localStorage.getItem('booking');
            let shouldShowDialog = false;
            
            if (bookingData) {
                try {
                    const booking = JSON.parse(bookingData);
                    console.log('Booking data check - name:', booking.name, 'phone:', booking.phone);
                    if (!booking.name || !booking.phone || booking.name.trim() === '' || booking.phone.trim() === '') {
                        shouldShowDialog = true;
                    }
                } catch (e) {
                    // If parsing fails, show dialog
                    shouldShowDialog = true;
                }
            } else {
                // No booking data, show dialog
                shouldShowDialog = true;
            }
            
            // Only show if conditions are met and dialog is not already showing
            if (shouldShowDialog && !showDiscountDialog) {
                console.log('Showing discount dialog - shouldShowDialog:', shouldShowDialog, 'showDiscountDialog:', showDiscountDialog);
                // Small delay to ensure page is fully rendered
                const timer = setTimeout(() => {
                    // Double-check the flag before showing (in case it was set while waiting)
                    const flagCheck = localStorage.getItem('userDetailsProvided');
                    if (flagCheck !== 'true') {
                        setShowDiscountDialog(true);
                    } else {
                        console.log('Flag was set while waiting, not showing dialog');
                    }
                }, 500);
                
                return () => clearTimeout(timer);
            } else {
                console.log('Not showing dialog - shouldShowDialog:', shouldShowDialog, 'showDiscountDialog:', showDiscountDialog);
            }
        }
    }, [loading, flights]);

    // Handle unlock discounted fare
    const handleUnlockDiscount = async () => {
        setDialogError('');
        
        const trimmedName = (dialogName || '').trim();
        if (!trimmedName || trimmedName.split(/\s+/).length < 2) {
            setDialogError('Please enter your full name (first and last name)');
            return;
        }

        if (!dialogPhone || !isValidPhoneNumber(dialogPhone)) {
            setDialogError('Please enter a valid UK mobile number starting with 07 (11 digits)');
            return;
        }

        // Update booking data in localStorage
        const bookingData = localStorage.getItem('booking');
        if (bookingData) {
            const booking = JSON.parse(bookingData);
            booking.name = dialogName;
            booking.phone = dialogPhone;
            localStorage.setItem('booking', JSON.stringify(booking));

            // Save booking search to database (for admin panel)
            try {
                const bookingSearchParams = {
                    departure: booking.departure,
                    arrival: booking.arrival,
                    departure_name: booking.departure_name,
                    arrival_name: booking.arrival_name,
                    date: booking.date,
                    return_date: booking.return_date || '',
                    full_name: dialogName,
                    phone: dialogPhone,
                    adult: booking.adults || '1',
                    children: booking.children || '0',
                    infants: booking.infants || '0',
                    status: 'SEARCH',
                    reviews: '',
                    description: `${booking.flight_type === 'return' ? 'Round Trip' : 'One Way'} - ${booking.flight_class || 'economy'}`,
                    flight_type: booking.flight_type || 'one-way',
                    flight_class: booking.flight_class || 'economy'
                };
                
                const bookingResponse = await service.addBooking(bookingSearchParams);
                console.log('Booking search saved to database:', bookingResponse.data);
                
                // Store the booking ID for later use
                if (bookingResponse.data && bookingResponse.data.insertId) {
                    localStorage.setItem('bookingresult', JSON.stringify(bookingResponse.data));
                }
            } catch (error) {
                console.error('Error saving booking to database:', error);
                // Don't show error to user, just log it
            }
        }

        // Update local state
        setName(dialogName);
        setPhone(dialogPhone);

        // Mark that user has provided details (so dialog won't show again)
        localStorage.setItem('userDetailsProvided', 'true');
        console.log('userDetailsProvided flag set to true');

        // Close dialog
        setShowDiscountDialog(false);
        setOutsideClickCount(0);
    };

    // Helper function to format date - ensures correct date display regardless of timezone
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            // Extract date part from ISO string to avoid timezone conversion
            // Handle both "2025-12-16" and "2025-12-16T19:00:00.000Z" formats
            let datePart = dateString;
            if (dateString.includes('T')) {
                datePart = dateString.split('T')[0];
            }
            
            // Remove any timezone info if present
            datePart = datePart.trim();
            
            // Parse the date part (YYYY-MM-DD format)
            const parts = datePart.split('-');
            if (parts.length !== 3) {
                throw new Error('Invalid date format');
            }
            
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            
            // Validate the date values
            if (isNaN(year) || isNaN(month) || isNaN(day)) {
                throw new Error('Invalid date values');
            }
            
            // Create date using UTC at noon to avoid day boundary issues
            const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            
            // Format using UTC timezone to ensure correct date display
            return utcDate.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric',
                timeZone: 'UTC'
            });
        } catch (error) {
            // Fallback: try to parse as-is and format using UTC
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return dateString; // Return original if invalid
                }
                // Extract date components using UTC to avoid timezone issues
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth();
                const day = date.getUTCDate();
                const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
                return utcDate.toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric',
                    timeZone: 'UTC'
                });
            } catch (e) {
                return dateString; // Return original if all parsing fails
            }
        }
    };

    const formatDateShort = (dateString) => {
        if (!dateString) return '';
        try {
            let datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
            const parts = datePart.trim().split('-');
            if (parts.length !== 3) return formatDate(dateString);
            const year = parseInt(parts[0], 10), month = parseInt(parts[1], 10), day = parseInt(parts[2], 10);
            if (isNaN(year) || isNaN(month) || isNaN(day)) return formatDate(dateString);
            const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            return utcDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
        } catch {
            return formatDate(dateString);
        }
    };

    // Handle clicks outside the dialog
    const handleBackdropClick = () => {
        const newCount = outsideClickCount + 1;
        setOutsideClickCount(newCount);
        if (newCount >= 3) {
            // Mark that user has dismissed the dialog (so it won't show again)
            localStorage.setItem('userDetailsProvided', 'true');
            console.log('Dialog dismissed by triple-click, setting userDetailsProvided flag');
            setShowDiscountDialog(false);
            setOutsideClickCount(0);
        }
    };

    // Handle clicks inside the dialog - reset counter
    const handleDialogContentClick = (e) => {
        e.stopPropagation();
        setOutsideClickCount(0);
    };

    useEffect(() => {

        const token = searchParams.get('package')
        const departure = searchParams.get('departure')
        const arrival = searchParams.get('arrival')
        const call = searchParams.get('call')

        // if(call){
        //   setRefresh(call)
        // }
        console.log('Package token:', token)
        console.log('Flight search params:', { departure, arrival })
        
        // Check if we have booking data in localStorage
        const hasBookingData = localStorage.getItem('booking');
        
        // Only redirect if neither package nor flight search params nor localStorage booking exists
        if (!token && !departure && !arrival && !hasBookingData) {
            // Preserve userDetailsProvided flag when clearing localStorage
            const userDetailsProvided = localStorage.getItem('userDetailsProvided');
            localStorage.clear();
            if (userDetailsProvided) {
                localStorage.setItem('userDetailsProvided', userDetailsProvided);
            }
            window.location.href = '/'
            return;
        }

        // Set package - could be an ID or "0" for new search
        if (token !== null && token !== undefined) {
            setPackage(token)
        }

    }, [window.location])

    const getAllparas = (id) => {
        service.getAllparas().then((response) => {
            let dep = response.data.filter((x) => x.package_id == id && x.para_type == 'OTHER');
            setParas(dep)

        });
    };

    // Load name and phone from localStorage on component mount
    useEffect(() => {
        const bookingData = localStorage.getItem('booking');
        if (bookingData) {
            try {
                const booking = JSON.parse(bookingData);
                if (booking.name) {
                    setName(booking.name);
                }
                if (booking.phone) {
                    setPhone(booking.phone);
                }
            } catch (error) {
                console.error('Error loading booking data:', error);
            }
        }
    }, []);

    useEffect(() => {

        if (packagee) {
            // Ensure progress indicator is visible when page loads
            setLoadingProgress({
                isLoading: true,
                resultsCount: 0,
                progressPercent: 0,
                startTime: Date.now()
            });
            
            let pp = {}
            if (packagee == 0) {

                let params = localStorage.getItem('booking');
                if (params) {
                    setLoading(true)
                    params = JSON.parse(params);

                    console.log(params, 'params')
                    pp = params;
                    setBookinginfo(pp);
                    searchFlights(pp)
                } else {
                    // No booking params, hide progress
                    setLoadingProgress({
                        isLoading: false,
                        resultsCount: 0,
                        progressPercent: 0,
                        startTime: null
                    });
                }
            } else {
                setLoading(true)
                service.getpackagebyid({ uid: packagee }).then(ree => {
                    console.log(ree.data)
                    if (ree.data.length > 0) {
                        pp.arrival = ree.data[0].arr_iatacode;
                        pp.departure = ree.data[0].dep_iatacode;
                        setSelectedPackage(ree.data[0])
                    }
                    setBookinginfo(pp)
                    searchFlights(pp)
                })
            }

            // service.postGetFlights({
            //     package:packagee,
            //     ...pp
            // }).then(ress=>{
            //     setLoading(false)
            //     console.log(ress.data.flights);
            //     setFlights(ress.data.flights)
            //     setPackageData(ress.data.package)
            //     getAllparas(ress.data.package.uid)
            // }).catch(err=>{
            //     setLoading(false)
            // })

            console.log(pp, 'pppppp')


        }

    }, [packagee])


    useEffect(() => {
        if (selectedSlice && selectedSlice.length) {
            searchFlights('return')
        }

    }, [selectedSlice])



    const searchFlights = async (pp) => {
        //   service.getSearchFlights({
        //     arrival:pp.arrival,
        //     departure:pp.departure
        // }).then(ress=>{

        //   setTimeout(()=>{setLoading(false)},1000)

        //     console.log(ress.data.flights);
        //     setFlights(ress.data)
        //    // setPackageData(ress.data.package)
        //    // getAllparas(ress.data.package.uid)
        //    //document.getElementById("flightdetail").scrollIntoView();

        // }).catch(err=>{
        //     setLoading(false)
        //  })

        let offerid = localStorage.getItem('offer')
        let bookinginfo = localStorage.getItem('booking')
        if (bookinginfo) {
            try {
                bookinginfo = JSON.parse(bookinginfo);
            } catch (e) {
                console.error('Error parsing bookinginfo:', e);
                bookinginfo = null;
            }
        }

        // Check if offer data exists
        if (!offerid || offerid === 'null') {
            // Guard: avoid duplicate fetch when searchFlights runs twice (e.g. React Strict Mode)
            if (searchInProgressRef.current) {
                return;
            }
            if (!pp || !pp.arrival || !pp.departure) {
                setLoadingProgress({ isLoading: false, resultsCount: 0, progressPercent: 0, startTime: null });
                setLoading(false);
                setFlights([]);
                setOffers([]);
                return;
            }
            console.log('No offer data found in localStorage. Fetching flights from API...', pp);
            searchInProgressRef.current = true;
            
            // If we have booking params, fetch flights from API
            if (pp && pp.arrival && pp.departure) {
                // Start progressive loading
                setLoadingProgress({
                    isLoading: true,
                    resultsCount: 0,
                    progressPercent: 0,
                    startTime: Date.now()
                });
                
                // Fetch both Duffel and Amadeus flights in parallel (same as deals)
                const duffelPromise = service.postGetFlightOfferDuffel({
                    arrival: pp.arrival,
                    departure: pp.departure,
                    date: pp.date,
                    return_date: pp.return_date || '',
                    adults: pp.adults || '1',
                    children: pp.children || '0',
                    infants: pp.infants || '0',
                    flight_type: pp.flight_type || 'one-way',
                    flight_class: pp.flight_class || 'economy'
                }).catch(err => {
                    console.error('Error fetching Duffel flights:', err);
                    return { data: { oneway: { offers: [] }, return: { offers: [] } } };
                });
                
                const amadeusPromise = service.postGetFlights({
                    arrival: pp.arrival,
                    departure: pp.departure,
                    date: pp.date,
                    return_date: pp.return_date || '',
                    adult: pp.adults || '1'
                }).catch(err => {
                    console.error('Error fetching Amadeus flights:', err);
                    return { data: { oneway: [], return: [] } };
                });
                
                Promise.all([duffelPromise, amadeusPromise]).then(async ([duffelResponse, amadeusResponse]) => {
                    console.log('✅ Duffel results:', duffelResponse.data);
                    console.log('✅ Amadeus results:', amadeusResponse.data);
                    
                    const duffelData = duffelResponse.data || {};
                    const amadeusData = amadeusResponse.data || {};
                    const combinedOffers = {
                        oneway: {
                            duffel: duffelData?.oneway?.offers || [],
                            amadeus: amadeusData?.oneway || [],
                            flightlabs: []
                        },
                        return: {
                            duffel: duffelData?.return?.offers || [],
                            amadeus: amadeusData?.return || [],
                            flightlabs: []
                        }
                    };
                    
                    const duffelCount = combinedOffers.oneway.duffel?.length || 0;
                    const amadeusCount = combinedOffers.oneway.amadeus?.length || 0;
                    console.log(`📦 Combined offers: ${duffelCount} Duffel + ${amadeusCount} Amadeus = ${duffelCount + amadeusCount} total flights`);
                    
                    // Store combined offers in localStorage
                    localStorage.setItem('offer', JSON.stringify(combinedOffers));
                    searchInProgressRef.current = false;
                    
                    // Now recursively call searchFlights - it will read from localStorage and process both Duffel and Amadeus
                    // The recursive call will find the offer in localStorage and skip the API fetch, going straight to processing
                    searchFlights(pp);
                    return;
                }).catch(err => {
                    console.error('Error fetching flights from API:', err);
                    searchInProgressRef.current = false;
                    setLoadingProgress({
                        isLoading: false,
                        resultsCount: 0,
                        progressPercent: 0,
                        startTime: null
                    });
                    setLoading(false);
                    setFlights([]);
                    setOffers([]);
                });
                return;
            } else {
                console.log('No booking params available');
                setLoading(false);
                setFlights([]);
                setOffers([]);
                return;
            }
        }

        // service.getFlightsDetailsByOfferId({
        //     offerid
        // }).then(ress => {

        // Start progressive loading for cached data
        setLoadingProgress({
            isLoading: true,
            resultsCount: 0,
            progressPercent: 0,
            startTime: Date.now()
        });

        setTimeout(() => { setLoading(false) }, 1000)

        let offr = [];
        let offersFromLocal = JSON.parse(offerid)
        console.log('🔍 DEBUG: Raw offersFromLocal:', JSON.stringify(offersFromLocal, null, 2));
        console.log('🔍 DEBUG: oneway type:', typeof offersFromLocal?.oneway);
        console.log('🔍 DEBUG: oneway value:', offersFromLocal?.oneway);
        console.log('🔍 DEBUG: oneway keys:', offersFromLocal?.oneway ? Object.keys(offersFromLocal.oneway) : 'N/A');
        console.log('🔍 DEBUG: oneway.duffel exists?', offersFromLocal?.oneway?.duffel !== undefined);
        console.log('🔍 DEBUG: oneway.amadeus exists?', offersFromLocal?.oneway?.amadeus !== undefined);
        console.log('🔍 DEBUG: oneway.offers exists?', offersFromLocal?.oneway?.offers !== undefined);
        console.log('🔍 DEBUG: oneway.duffel value:', offersFromLocal?.oneway?.duffel);
        console.log('🔍 DEBUG: oneway.amadeus value:', offersFromLocal?.oneway?.amadeus);
        console.log('🔍 DEBUG: oneway.offers value:', offersFromLocal?.oneway?.offers);
        
        // Check if offersFromLocal has the new combined structure (with duffel/amadeus/flightlabs) or old structure
        let duffelOnewayOffers = [];
        let amadeusOnewayOffers = [];
        let flightlabsOnewayOffers = [];
        let duffelReturnOffers = [];
        let amadeusReturnOffers = [];
        let flightlabsReturnOffers = [];
        
        // Check for new combined structure first
        if (offersFromLocal && typeof offersFromLocal === 'object' && offersFromLocal !== null) {
            const oneway = offersFromLocal.oneway;
            
            // Check if oneway exists and is an object
            if (oneway && typeof oneway === 'object' && oneway !== null && !Array.isArray(oneway)) {
                // Check if it has duffel or amadeus or flightlabs keys (new structure)
                const hasDuffel = oneway.duffel !== undefined || 'duffel' in oneway || oneway.hasOwnProperty('duffel');
                const hasAmadeus = oneway.amadeus !== undefined || 'amadeus' in oneway || oneway.hasOwnProperty('amadeus');
                const hasFlightlabs = oneway.flightlabs !== undefined || 'flightlabs' in oneway || oneway.hasOwnProperty('flightlabs');
                
                console.log('🔍 DEBUG: hasDuffel:', hasDuffel, 'hasAmadeus:', hasAmadeus, 'hasFlightlabs:', hasFlightlabs);
                
                if (hasDuffel || hasAmadeus || hasFlightlabs) {
                    // New combined structure
                    console.log('✅ SUCCESS: Detected new combined structure');
                    duffelOnewayOffers = Array.isArray(oneway.duffel) ? oneway.duffel : (oneway.duffel ? [oneway.duffel] : []);
                    amadeusOnewayOffers = Array.isArray(oneway.amadeus) ? oneway.amadeus : (oneway.amadeus ? [oneway.amadeus] : []);
                    flightlabsOnewayOffers = [];
                    
                    const returnData = offersFromLocal.return;
                    if (returnData && typeof returnData === 'object' && returnData !== null && !Array.isArray(returnData)) {
                        duffelReturnOffers = Array.isArray(returnData.duffel) ? returnData.duffel : (returnData.duffel ? [returnData.duffel] : []);
                        amadeusReturnOffers = Array.isArray(returnData.amadeus) ? returnData.amadeus : (returnData.amadeus ? [returnData.amadeus] : []);
                        flightlabsReturnOffers = [];
                    }
                } 
                // Check if it has offers key (old structure)
                else if (oneway.offers !== undefined || 'offers' in oneway || oneway.hasOwnProperty('offers')) {
                    // Old structure (Duffel only)
                    console.log('✅ SUCCESS: Detected old structure (Duffel only)');
                    duffelOnewayOffers = Array.isArray(oneway.offers) ? oneway.offers : (oneway.offers ? [oneway.offers] : []);
                    
                    const returnData = offersFromLocal.return;
                    if (returnData && typeof returnData === 'object' && returnData !== null && !Array.isArray(returnData)) {
                        duffelReturnOffers = Array.isArray(returnData.offers) ? returnData.offers : (returnData.offers ? [returnData.offers] : []);
                    }
                } else {
                    console.error('❌ ERROR: Invalid offer data structure - oneway is not in expected format');
                    console.error('❌ ERROR: oneway structure:', JSON.stringify(oneway, null, 2));
                    console.error('❌ ERROR: oneway keys:', Object.keys(oneway || {}));
                    console.error('❌ ERROR: Full offersFromLocal:', JSON.stringify(offersFromLocal, null, 2));
                    setFlights([]);
                    setOffers([]);
                    return;
                }
            } else {
                console.error('❌ ERROR: Invalid offer data structure - oneway is missing, null, or not an object');
                console.error('❌ ERROR: oneway value:', oneway);
                console.error('❌ ERROR: oneway type:', typeof oneway);
                console.error('❌ ERROR: offersFromLocal:', JSON.stringify(offersFromLocal, null, 2));
                setFlights([]);
                setOffers([]);
                return;
            }
        } else {
            console.error('❌ ERROR: Invalid offer data structure - offersFromLocal is not an object');
            console.error('❌ ERROR: offersFromLocal:', offersFromLocal);
            console.error('❌ ERROR: offersFromLocal type:', typeof offersFromLocal);
            setFlights([]);
            setOffers([]);
            return;
        }
        
        console.log('Processed offers - Duffel oneway:', duffelOnewayOffers.length, 'Amadeus oneway:', amadeusOnewayOffers.length);
        console.log('Total offers to process:', duffelOnewayOffers.length + amadeusOnewayOffers.length);
        
        // Update progress - found offers
        const totalOffers = duffelOnewayOffers.length + amadeusOnewayOffers.length;
        setLoadingProgress(prev => ({
            ...prev,
            resultsCount: totalOffers,
            progressPercent: Math.min(totalOffers > 0 ? 50 : 20, 80)
        }));
        
        const totalPassengers = parseInt(pp?.adults || '1') + parseInt(pp?.children || '0') + parseInt(pp?.infants || '0');
        
        // Fetch and match return offers for Duffel flights if return_date exists
        console.log('🚀 START: About to check for return offers');
        console.log('🔍 DEBUG: pp object:', pp);
        console.log('🔍 DEBUG: bookinginfo object:', bookinginfo);
        console.log('🔍 DEBUG: pp.return_date:', pp?.return_date);
        console.log('🔍 DEBUG: pp.returnDate:', pp?.returnDate);
        console.log('🔍 DEBUG: bookinginfo?.return_date:', bookinginfo?.return_date);
        console.log('🔍 DEBUG: bookinginfo?.returnDate:', bookinginfo?.returnDate);
        let returnOffersMap = new Map();
        // Check both snake_case and camelCase
        const returnDate = bookinginfo?.return_date || bookinginfo?.returnDate || pp?.return_date || pp?.returnDate;
        if (returnDate && duffelOnewayOffers.length > 0) {
            returnOffersMap = await fetchAndMatchDuffelReturnOffers(duffelOnewayOffers, {
                return_date: returnDate,
                arrival: bookinginfo?.arrival || pp?.arrival,
                departure: bookinginfo?.departure || pp?.departure,
                adults: bookinginfo?.adults || pp?.adults || '1',
                children: bookinginfo?.children || pp?.children || '0',
                infants: bookinginfo?.infants || pp?.infants || '0',
                flight_class: bookinginfo?.flight_class || pp?.flight_class || 'economy'
            });
        }
        
        // Process Duffel offers
        for (let i = 0; i < duffelOnewayOffers.length; i++) {
            // Update progress as we process each flight
            if (i % 5 === 0 || i === duffelOnewayOffers.length - 1) {
                const progress = 50 + Math.floor((i / duffelOnewayOffers.length) * 20);
                setLoadingProgress(prev => ({
                    ...prev,
                    progressPercent: Math.min(progress, 85)
                }));
            }
            
            let f = duffelOnewayOffers[i];

            // Skip flights with 0 fare
            if (!f.total_amount || parseFloat(f.total_amount) <= 0) continue;

            let pricePerPerson = parseFloat(f.total_amount);
            let amountorg = pricePerPerson * totalPassengers;
            let discountPercent = (amountorg / 100) * 0;
            let amountnew = amountorg - discountPercent;
            amountnew = amountnew.toFixed(2);
            
            // Extract all unique airlines from all segments in all slices
            const uniqueAirlines = new Map();
            (f.slices || []).forEach(slice => {
                (slice.segments || []).forEach(segment => {
                    const carrierCode = segment.operating_carrier?.iata_code || segment.marketing_carrier?.iata_code;
                    if (carrierCode && !uniqueAirlines.has(carrierCode)) {
                        uniqueAirlines.set(carrierCode, {
                            code: carrierCode,
                            logo: getAirlineLogo(carrierCode)
                        });
                    }
                });
            });
            const allAirlines = Array.from(uniqueAirlines.values());
            
            // Extract baggage information from Duffel offer
            // Baggage is located at: offer.slices[].segments[].passengers[].baggages[]
            let baggageInfo = '';
            let baggageChecked = null; // e.g. "1" or "23KG"
            let baggageCabin = null;   // e.g. "1" or "7KG"
            let checkedBaggageWeight = 0;
            
            // Get baggage from first slice, first segment, first passenger (most common case)
            const firstSlice = f.slices?.[0];
            const firstSegment = firstSlice?.segments?.[0];
            const firstPassenger = firstSegment?.passengers?.[0];
            const baggages = firstPassenger?.baggages || [];
            
            if (baggages.length > 0) {
                // Count checked and carry-on bags
                let checkedCount = 0;
                let carryOnCount = 0;
                
                baggages.forEach(baggage => {
                    const quantity = baggage.quantity || 0;
                    if (baggage.type === 'checked') {
                        checkedCount += quantity;
                        // If weight is available, use it
                        if (baggage.weight_kg) {
                            checkedBaggageWeight = baggage.weight_kg;
                        } else if (baggage.weight_limit_kg) {
                            checkedBaggageWeight = baggage.weight_limit_kg;
                        }
                    } else if (baggage.type === 'carry_on') {
                        carryOnCount += quantity;
                    }
                });
                
                // Format baggage strings similar to Amadeus format
                if (checkedCount > 0) {
                    baggageChecked = checkedBaggageWeight > 0 
                        ? `${checkedBaggageWeight}KG` 
                        : String(checkedCount);
                }
                if (carryOnCount > 0) {
                    baggageCabin = String(carryOnCount);
                }
                
                // Create combined baggage info string
                const baggageParts = [];
                if (baggageChecked) baggageParts.push(`${baggageChecked} checked`);
                if (baggageCabin) baggageParts.push(`${baggageCabin} cabin`);
                baggageInfo = baggageParts.join(', ');
            }
            
            // For Duffel, individual fares are not available - only total_amount
            // We'll set passengerFares to null to indicate total price only
            let p = {
                id: f.id,
                id2: i,
                source: 'Duffel',
                origin_iatacode: f.slices[0].origin.iata_code,
                origin_name: f.slices[0].origin.name,
                origin_city_name: f.slices[0].origin.city_name,
                origin_segments: mapDuffelSegments(f.slices[0].segments),
                destination_iatacode: f.slices[0].destination.iata_code,
                destination_name: f.slices[0].destination.name,
                destination_city_name: f.slices[0].destination.city_name,
                duration: extractTime(f.slices[0].duration),
                amount: amountnew,
                amount1: amountnew,
                airline: f.owner.name,
                airline_logo: f.owner.logo_symbol_url,
                all_airlines: allAirlines, // Array of all airlines in this flight
                baggage: baggageInfo,
                baggage_checked: baggageChecked,
                baggage_cabin: baggageCabin,
                baggage_weight: checkedBaggageWeight, // For sorting/filtering
                passengerFares: null, // Duffel doesn't provide individual fares - only total
            }
            if (bookinginfo.return_date) p.is_return_offer_include = true;
            // Check if offer has return slice (from combined round-trip request)
            if (f.slices && f.slices.length > 1 && f.slices[1].segments) {
                p.return_segments = mapDuffelSegments(f.slices[1].segments);
            }
            // Match return offer by airline (cheapest return for same airline)
            else if (bookinginfo.return_date && f.owner?.name) {
                const normalizedAirline = f.owner.name.trim().toLowerCase();
                if (returnOffersMap.has(normalizedAirline)) {
                    const returnOffer = returnOffersMap.get(normalizedAirline);
                    if (returnOffer?.slices?.[0]?.segments) {
                        p.return_segments = mapDuffelSegments(returnOffer.slices[0].segments);
                        // Update total amount to include return
                        const returnPrice = parseFloat(returnOffer.total_amount || 0);
                        const newTotal = parseFloat(amountnew) + (returnPrice * totalPassengers);
                        p.amount = newTotal.toFixed(2);
                        p.amount1 = newTotal.toFixed(2);
                    }
                }
            }
            offr.push(p)
        }
        
        // Process Amadeus offers
        for (let i = 0; i < amadeusOnewayOffers.length; i++) {
            // Update progress as we process each flight
            if (i % 5 === 0 || i === amadeusOnewayOffers.length - 1) {
                const baseProgress = duffelOnewayOffers.length > 0 ? 70 : 50;
                const amadeusProgress = Math.floor((i / amadeusOnewayOffers.length) * 20);
                setLoadingProgress(prev => ({
                    ...prev,
                    progressPercent: Math.min(baseProgress + amadeusProgress, 90)
                }));
            }
            
            let f = amadeusOnewayOffers[i];

            // Skip flights with 0 fare
            const rawPrice = parseFloat(f.price?.grandTotal || f.price?.total || 0);
            if (rawPrice <= 0) continue;

            // Amadeus price is already total, convert to number and apply discount
            let priceTotal = rawPrice;
            // Convert to per person if needed (Amadeus might return total)
            let pricePerPerson = priceTotal / totalPassengers;
            let amountorg = pricePerPerson * totalPassengers;
            let discountPercent = (amountorg / 100) * 0;
            let amountnew = amountorg - discountPercent;
            amountnew = amountnew.toFixed(2);
            
            // Extract segments from Amadeus format
            const segments = f.itineraries?.[0]?.segments || [];
            const firstSegment = segments[0] || {};
            const lastSegment = segments[segments.length - 1] || {};
            
            // Calculate duration from segments
            let totalDuration = { hours: 0, minutes: 0 };
            if (f.itineraries?.[0]?.duration) {
                const durationStr = f.itineraries[0].duration;
                const match = durationStr.match(/PT(\d+)H(\d+)M/);
                if (match) {
                    totalDuration = { hours: parseInt(match[1]), minutes: parseInt(match[2]) };
                }
            }
            
            // Get airline from first segment
            const airline = firstSegment.carrierCode || '';
            const airlineName = segments.find(s => s.carrierCode)?.carrierCode || '';
            
            // Extract baggage information and individual fares from travelerPricings
            let baggageInfo = '';
            let baggageChecked = null; // e.g. "23KG" or "1"
            let baggageCabin = null;   // e.g. "1" or "7KG"
            
            // Extract individual passenger fares (Amadeus provides this)
            let passengerFares = {
                adult: null,
                child: null,
                infant: null,
                currency: f.price?.currency || 'GBP'
            };
            
            if (f.travelerPricings && f.travelerPricings.length > 0) {
                const firstTraveler = f.travelerPricings[0];
                if (firstTraveler.fareDetailsBySegment && firstTraveler.fareDetailsBySegment.length > 0) {
                    const fareDetails = firstTraveler.fareDetailsBySegment[0];
                    const checkedBags = fareDetails.includedCheckedBags;
                    const cabinBags = fareDetails.includedCabinBags;
                    if (checkedBags) {
                        baggageChecked = checkedBags.weight
                            ? `${checkedBags.weight}${checkedBags.weightUnit || 'KG'}`
                            : (checkedBags.quantity ? String(checkedBags.quantity) : null);
                    }
                    if (cabinBags) {
                        baggageCabin = cabinBags.weight
                            ? `${cabinBags.weight}${cabinBags.weightUnit || 'KG'}`
                            : (cabinBags.quantity ? String(cabinBags.quantity) : null);
                    }
                    let baggageParts = [];
                    if (baggageChecked) baggageParts.push(`${baggageChecked} checked`);
                    if (baggageCabin) baggageParts.push(`${baggageCabin} cabin`);
                    baggageInfo = baggageParts.join(', ');
                }
                
                // Extract individual fares for each passenger type
                f.travelerPricings.forEach(traveler => {
                    const fareAmount = parseFloat(traveler.price?.total || 0);
                    if (traveler.travelerType === 'ADULT') {
                        passengerFares.adult = fareAmount;
                    } else if (traveler.travelerType === 'CHILD') {
                        passengerFares.child = fareAmount;
                    } else if (traveler.travelerType === 'HELD_INFANT' || traveler.travelerType === 'SEATED_INFANT') {
                        passengerFares.infant = fareAmount;
                    }
                });
            }
            
            // Create a unique key for grouping same flights
            // Use only carrier+flight number and departure date (not time) to allow grouping
            const flightKey = segments.map(seg => {
                // Extract just the date portion, not the time
                const depDate = seg.departure?.at ? seg.departure.at.split('T')[0] : '';
                return `${seg.carrierCode}${seg.number}-${depDate}`;
            }).join('_');
            
            // Debug first few flights
            if (i < 3) {
                console.log(`🔍 Amadeus flight ${i} grouping:`, {
                    flightKey: flightKey,
                    carrier: firstSegment.carrierCode,
                    number: firstSegment.number,
                    departureAt: firstSegment.departure?.at,
                    price: f.price?.total,
                    baggage: f.travelerPricings?.[0]?.fareDetailsBySegment?.[0]?.includedCheckedBags
                });
            }
            
            // Extract checked baggage weight for comparison
            let checkedBaggageWeight = 0;
            if (f.travelerPricings?.[0]?.fareDetailsBySegment?.[0]?.includedCheckedBags?.weight) {
                checkedBaggageWeight = f.travelerPricings[0].fareDetailsBySegment[0].includedCheckedBags.weight;
            }
            
            // Extract all unique airlines from all segments
            const uniqueAirlines = new Map();
            segments.forEach(seg => {
                const carrierCode = seg.carrierCode;
                if (carrierCode && !uniqueAirlines.has(carrierCode)) {
                    uniqueAirlines.set(carrierCode, {
                        code: carrierCode,
                        logo: getAirlineLogo(carrierCode)
                    });
                }
            });
            const allAirlines = Array.from(uniqueAirlines.values());
            
            let p = {
                id: f.id || `amadeus_${i}`,
                id2: duffelOnewayOffers.length + i,
                source: 'Amadeus',
                flightKey: flightKey, // For grouping same flights
                origin_iatacode: firstSegment.departure?.iataCode || pp?.departure || '',
                origin_name: firstSegment.departure?.iataCode || '',
                origin_city_name: firstSegment.departure?.iataCode || pp?.departure_name || '',
                origin_segments: segments.map(seg => ({
                    departing_at: seg.departure?.at,
                    arriving_at: seg.arrival?.at,
                    origin: { iata_code: seg.departure?.iataCode, name: seg.departure?.iataCode },
                    destination: { iata_code: seg.arrival?.iataCode, name: seg.arrival?.iataCode },
                    duration: seg.duration || '',
                    marketing_carrier: { name: seg.carrierCode || '' }
                })),
                destination_iatacode: lastSegment.arrival?.iataCode || pp?.arrival || '',
                destination_name: lastSegment.arrival?.iataCode || '',
                destination_city_name: lastSegment.arrival?.iataCode || pp?.arrival_name || '',
                duration: totalDuration,
                amount: amountnew,
                amount1: amountnew,
                amount_original: priceTotal, // Store original price for comparison
                airline: airlineName || airline || 'Unknown',
                airline_logo: getAirlineLogo(airline || airlineName),
                all_airlines: allAirlines, // Array of all airlines in this flight
                baggage: baggageInfo,
                baggage_checked: baggageChecked,
                baggage_cabin: baggageCabin,
                baggage_weight: checkedBaggageWeight, // For sorting/filtering
                fareOption: f.travelerPricings?.[0]?.fareOption || 'STANDARD',
                numberOfBookableSeats: f.numberOfBookableSeats || 0,
                rawAmadeusOffer: f, // Store raw offer for later use
                passengerFares: passengerFares, // Individual fares for adults, children, infants
            }
            if (bookinginfo.return_date) p.is_return_offer_include = true;
            const returnSegs = f.itineraries?.[1]?.segments || [];
            if (returnSegs.length > 0) {
                p.return_segments = returnSegs.map(seg => ({
                    departing_at: seg.departure?.at,
                    arriving_at: seg.arrival?.at,
                    origin: { iata_code: seg.departure?.iataCode, name: seg.departure?.iataCode },
                    destination: { iata_code: seg.arrival?.iataCode, name: seg.arrival?.iataCode },
                    duration: seg.duration || '',
                    marketing_carrier: { name: seg.carrierCode || '' }
                }));
            }
            offr.push(p)
        }
        
        let return_offers = JSON.parse(offerid);
        return_offers = return_offers.return?.duffel || return_offers.return?.offers || [];

            // if (return_offers) {
            //     let findReturn = return_offers.filter(ff => ff.owner.name == p.airline);
            //     findReturn = findReturn.sort((a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount));
            //     findReturn.map(fff => {
            //         let newoffr = { ...p };
            //         newoffr.amount = parseFloat(p.amount) + parseFloat(fff.total_amount)
            //         newoffr.return_amount = parseFloat(fff.total_amount)
            //         newoffr.amount11 = parseFloat(p.amount1)
            //         newoffr.is_return_offer_include = true
            //         offr.push(newoffr);
            //     })
            //     // if (findReturn && findReturn.length > 0) {
            //     //     findReturn = findReturn[0];
            //     //     p.amount = parseFloat(p.amount) + parseFloat(findReturn.total_amount)
            //     //     p.return_amount = parseFloat(findReturn.total_amount)
            //     //     p.amount11 = parseFloat(p.amount1)
            //     //     p.is_return_offer_include = true
            //     // }
            // } else {
            //     offr.push(p)
            // }

            // if (f.slices.length > 1) {
            //     p.return_origin_iatacode = f.slices[1].origin.iata_code;
            //     p.return_origin_name = f.slices[1].origin.name;
            //     p.return_origin_city_name = f.slices[1].origin.city_name;
            //     p.return_destination_iatacode = f.slices[1].destination.iata_code;
            //     p.return_destination_name = f.slices[1].destination.name;
            //     p.return_destination_city_name = f.slices[1].destination.city_name;
            //     p.return_duration = f.slices[1].duration;
            //     p.return_segments = f.slices[1].segments;
            // }

        setFlights(offr)
        setFlightsOrg(offr)
        setFirstSort(true);
        
        // Complete loading progress with a small delay for smooth transition
        setTimeout(() => {
            setLoadingProgress({
                isLoading: false,
                resultsCount: offr.length,
                progressPercent: 100,
                startTime: null
            });
        }, 300);

        // setPackageData(ress.data.package)
        // getAllparas(ress.data.package.uid)
        //document.getElementById("flightdetail").scrollIntoView();

        // }).catch(err => {
        //     setLoading(false)
        // })

    }

    // Progressive loading simulation - updates progress over time
    useEffect(() => {
        if (!loadingProgress.isLoading || !loadingProgress.startTime) return;
        
        const interval = setInterval(() => {
            const elapsed = Date.now() - loadingProgress.startTime;
            const elapsedSeconds = elapsed / 1000;
            
            // Simulate progress based on time elapsed
            // Progress increases gradually: 10% at 1s, 25% at 3s, 50% at 5s, 75% at 8s, 90% at 12s
            let timeBasedProgress = 0;
            if (elapsedSeconds < 1) {
                timeBasedProgress = 10;
            } else if (elapsedSeconds < 3) {
                timeBasedProgress = 10 + (elapsedSeconds - 1) * 7.5; // 10% to 25%
            } else if (elapsedSeconds < 5) {
                timeBasedProgress = 25 + (elapsedSeconds - 3) * 12.5; // 25% to 50%
            } else if (elapsedSeconds < 8) {
                timeBasedProgress = 50 + (elapsedSeconds - 5) * 8.33; // 50% to 75%
            } else if (elapsedSeconds < 12) {
                timeBasedProgress = 75 + (elapsedSeconds - 8) * 3.75; // 75% to 90%
            } else {
                timeBasedProgress = 90;
            }
            
            // Use the higher of time-based progress or results-based progress
            const currentProgress = Math.max(timeBasedProgress, loadingProgress.progressPercent);
            
            // Don't exceed 90% until we have results or loading completes
            if (loadingProgress.resultsCount === 0 && currentProgress > 90) {
                return; // Keep at 90% until results come in
            }
            
            setLoadingProgress(prev => ({
                ...prev,
                progressPercent: Math.min(currentProgress, prev.resultsCount > 0 ? 95 : 90)
            }));
        }, 200); // Update every 200ms
        
        return () => clearInterval(interval);
    }, [loadingProgress.isLoading, loadingProgress.startTime, loadingProgress.resultsCount, loadingProgress.progressPercent]);

    useEffect(() => {
        // Only auto-scroll if not loading AND no overlays are open AND we're not restoring scroll
        // This prevents scrolling when closing modals
        if (!loading && !showMobileBookingOverlay && !showAlternativesModal && !isRestoringScrollRef.current) {
            var scrollDiv = document.getElementById("flightdetail");
            if (scrollDiv) {
                var scrollDivTop = scrollDiv.offsetTop;
                // Only scroll if we're not already near that position (to avoid unnecessary scrolling)
                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                const scrollDiff = Math.abs(currentScroll - scrollDivTop);
                // Only scroll if we're more than 100px away from the target
                if (scrollDiff > 100) {
                    window.scrollTo({ top: scrollDivTop, behavior: 'smooth' });
                }
            }
        }

        // Clear airlines when loading starts
        if (loading) {
            setAirlines([]);
            return;
        }

        // Use flightsOrg (original unfiltered flights) to always show all airlines from current search
        if (!loading && flightsOrg && flightsOrg.length > 0) {
            let cancelled = false;
            (async () => {
                const gbpAmounts = await Promise.all(
                    flightsOrg.map((f) =>
                        convertToGBP(parseFloat(f.amount), f.currency === 'USD' ? 'USD' : 'GBP')
                    )
                );
                if (cancelled) return;
                const airlineMap = new Map();
                flightsOrg.forEach((flight, i) => {
                    if (flight.airline) {
                        const gbp = gbpAmounts[i];
                        if (!airlineMap.has(flight.airline)) {
                            airlineMap.set(flight.airline, {
                                value: flight.airline,
                                label: flight.airline,
                                logo: flight.airline_logo || '',
                                cheapestPkr: gbp
                            });
                        } else {
                            const current = airlineMap.get(flight.airline);
                            if (gbp < current.cheapestPkr) current.cheapestPkr = gbp;
                        }
                    }
                });
                const airlineOptions = Array.from(airlineMap.values()).sort(
                    (a, b) => (a.cheapestPkr || 0) - (b.cheapestPkr || 0)
                );
                if (!cancelled) setAirlines(airlineOptions);
            })();
            return () => { cancelled = true; };
        } else if (!loading && (!flightsOrg || flightsOrg.length === 0)) {
            setAirlines([]);
        }

    }, [loading, flightsOrg, showMobileBookingOverlay, showAlternativesModal])

    const submit = (e) => {
        e.preventDefault()
        let booking = localStorage.getItem('bookingresult')
        let bookingData = localStorage.getItem('booking')

        // Allow submission if we have either a saved booking result OR raw booking data OR a selected package
        if (!booking && !bookingData && !selectedPackage) {
            setError('Submission failed - no booking data found. Please search again.');
            showToast(true);
            setTimeout(() => { showToast(false); }, 4000)
            return;
        }

        const trimmedName = (name || '').trim();
        if (!trimmedName || trimmedName.split(/\s+/).length < 2) {
            setError('Please enter your full name (first and last name)');
            showToast(true);
            setTimeout(() => { showToast(false); }, 4000)
            return;
        }
        if (!phone || !isValidPhoneNumber(phone)) {
            setError('Please enter a valid UK mobile number starting with 07 (11 digits)');
            showToast(true);
            setTimeout(() => { showToast(false); }, 4000)
            return;
        }
        if (!email || !isValidEmail(email)) {
            setError('Please enter a valid email address');
            showToast(true);
            setTimeout(() => { showToast(false); }, 4000)
            return;
        }

        if (selectedPackage) {
            submitOnSelectedPkg()
            return;
        }

        const afterSuccess = () => {
            setSubmitted(true)
            const userDetailsProvided = localStorage.getItem('userDetailsProvided');
            localStorage.clear();
            if (userDetailsProvided) {
                localStorage.setItem('userDetailsProvided', userDetailsProvided);
            }
            window.location.href = '/thankyou'
        }

        if (booking) {
            // Update the existing booking record with contact details + NEW status
            booking = JSON.parse(booking);
            let params = {
                full_name: name,
                phone,
                email,
                payment_type,
                uid: booking.insertId,
                selectedSlice,
                status: 'NEW'
            }
            service.addBooking(params).then(afterSuccess).catch(err => {
                console.log(err);
            })
        } else {
            // No prior DB record (table may have been missing at search time) — do a fresh insert
            const raw = bookingData ? JSON.parse(bookingData) : {};
            let params = {
                departure: raw.departure || '',
                arrival: raw.arrival || '',
                departure_name: raw.departure_name || '',
                arrival_name: raw.arrival_name || '',
                date: raw.date || '',
                return_date: raw.return_date || '',
                full_name: name,
                phone,
                email,
                payment_type,
                adult: raw.adults || 1,
                children: raw.children || 0,
                infants: raw.infants || 0,
                status: 'NEW',
                reviews: '',
                description: `${raw.flight_type === 'return' ? 'Round Trip' : 'One Way'} - ${raw.flight_class || 'economy'}`,
                flight_type: raw.flight_type || 'one-way',
                flight_class: raw.flight_class || 'economy',
                selectedSlice
            }
            service.addBooking(params).then(afterSuccess).catch(err => {
                console.log(err);
                // Still navigate to thank you even if DB save fails
                afterSuccess();
            })
        }
    }
    function toJSONLocal(date) {
        var local = new Date(date);
        local.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        return local.toJSON().slice(0, 10);
    }
    const submitOnSelectedPkg = () => {


        let params = {
            departure: selectedPackage.dep_iatacode,
            arrival: selectedPackage.arr_iatacode,
            date: toJSONLocal(new Date()),
            full_name: name,
            phone,
            email,
            adult: 1,
            children: 0,
            infants: 0,
            status: 'NEW',
            reviews: '',
            description: selectedPackage.uid + ` Package`,
            return_date: toJSONLocal(new Date()),
            departure_name: selectedPackage.dep_name,
            arrival_name: selectedPackage.arrival_name
        }

        service.addBooking(params).then(x => {
            // document.getElementById('my_modal_4').showModal()
            // if(setShowToast){
            //   setShowToast(true);
            // }

            // Preserve userDetailsProvided flag when clearing localStorage
            const userDetailsProvided = localStorage.getItem('userDetailsProvided');
            localStorage.clear();
            if (userDetailsProvided) {
                localStorage.setItem('userDetailsProvided', userDetailsProvided);
            }
            setSubmitted(true)
        }).catch(err => {
            console.log(err);
        })
    }

    const sortOptions = [
        { value: 'least-exp', label: 'Least Expensive' },
        { value: 'most-exp', label: 'Most Expensive' },
        { value: 'shortest-duration', label: 'Shortest duration' },
        { value: 'longest-duration', label: 'Longest duration' },
    ]

    useEffect(() => {
        setVisibleFlightsCount(20);
        let filtered = [...flightsOrg];
        // Filter by selected airlines if any are selected
        if (selectedAirlines.length > 0) {
            filtered = flightsOrg.filter(a => selectedAirlines.includes(a.airline))
        }

        if (selectedSort == 'least-exp') {
            let sf = filtered.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
            console.log(sf, filtered, flightsOrg, 'sssssssssssssssssssff')
            setFlights(sf)
        } else if (selectedSort == 'most-exp') {
            let sf = filtered.sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
            setFlights(sf)
        }
        else if (selectedSort == 'shortest-duration') {
            let sf = filtered.sort((a, b) => {
                // First compare hours
                if (a.duration.hours !== b.duration.hours) {
                    return a.duration.hours - b.duration.hours;
                }
                // If hours are the same, compare minutes
                return a.duration.minutes - b.duration.minutes;
            });
            setFlights(sf)
        }
        else if (selectedSort == 'longest-duration') {
            let sf = filtered.sort((a, b) => {
                // First compare hours
                if (a.duration.hours !== b.duration.hours) {
                    return b.duration.hours - a.duration.hours;
                }
                // If hours are the same, compare minutes
                return b.duration.minutes - a.duration.minutes;
            });
            setFlights(sf)
        }
    }, [selectedSort, selectedAirlines, firstSort, flightsOrg])

    // Swipe up (scroll down) = hide airline strip, swipe down (scroll up) = show.
    useEffect(() => {
        if (!flights?.length) return;
        lastScrollYRef.current = window.scrollY ?? window.pageYOffset;
        const threshold = 12;
        let ticking = false;

        const handleScroll = () => {
            // Don't hide airline strip if any overlay/modal is open or loading
            if (showMobileBookingOverlay || showAlternativesModal || (loading && selectedFlightForBooking)) {
                return;
            }
            
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const y = window.scrollY ?? window.pageYOffset;
                const prev = lastScrollYRef.current;
                if (y <= 60) {
                    setAirlineStripVisible(true);
                } else if (y > prev + threshold) {
                    setAirlineStripVisible(false);
                } else if (y < prev - threshold) {
                    setAirlineStripVisible(true);
                }
                lastScrollYRef.current = y;
                ticking = false;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [flights?.length, showMobileBookingOverlay, showAlternativesModal, loading, selectedFlightForBooking])

    useEffect(() => () => {
        document.body.style.overflow = '';
    }, []);

    const flightsToShow = flights?.length ? flights.slice(0, visibleFlightsCount) : [];
    const hasMoreFlights = flights?.length > visibleFlightsCount;

    useEffect(() => {
        if (!hasMoreFlights || loadingMore || !loadMoreSentinelRef.current) return;
        const useScrollContainer = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
        const root = useScrollContainer ? flightsScrollContainerRef.current : null;
        const el = loadMoreSentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                setLoadingMore(true);
                setTimeout(() => {
                    setVisibleFlightsCount((prev) => Math.min(prev + 20, flights?.length ?? prev));
                    setLoadingMore(false);
                }, 400);
            },
            { root: root || null, rootMargin: '100px', threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMoreFlights, loadingMore, flights?.length, visibleFlightsCount]);

    return (
        // loading ? <div className="absolute z-[4] bg-white w-full h-[100vh] flex justify-center items-center gap-3">
        //     {/* <Rocket className="text-white text-lg"/> */}
        //     {/* <p className="text-white text-lg">🚀 Loading...</p> */}
        //     <img src={loadersvg} className="w-[70%] md:w-[40%]" />
        // </div> :
        <div className="relative">

            <Navbar bg={1} />

            {/* <BannerSection /> */}
            <div className=" flex justify-center items-center" id="flightdetail">
            </div>

            <div className="hidden md:block breadcrumbs text-sm w-full px-2 my-5 text-mLightGray mt-14 md:mt-14">
                <ul>
                    <li><a>Flights</a></li>
                    <li><a>{bookinginfo?.departure} - {bookinginfo?.arrival}</a></li>

                </ul>
            </div>

            {/* Change flight strip - Fixed below navbar. Swipe up = hide, swipe down = show (same as airline strip). */}
            {bookinginfo && flights && flights.length > 0 && (
                <div
                    className={`fixed left-0 right-0 top-14 z-40 w-full bg-white border-b border-gray-100 transition-transform duration-300 ease-out ${
                        airlineStripVisible ? 'translate-y-0' : '-translate-y-full'
                    }`}
                >
                    <div className="flex flex-wrap items-center justify-between gap-2 py-2 px-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="text-sm font-medium text-gray-700 truncate">{bookinginfo.departure}</span>
                            {bookinginfo.return_date ? (
                                <ArrowRightLeft className="w-4 h-4 shrink-0 text-gray-500" />
                            ) : (
                                <ArrowRight className="w-4 h-4 shrink-0 text-gray-500" />
                            )}
                            <span className="text-sm font-medium text-gray-700 truncate">{bookinginfo.arrival}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowChangeFlightOverlay(true);
                                    document.body.style.overflow = 'hidden';
                                }}
                                className="shrink-0 rounded-full bg-navy-50 px-3 py-1.5 text-sm font-medium text-secondary hover:bg-navy-100 active:scale-95 active:opacity-90 transition-all duration-150"
                            >
                                {bookinginfo.return_date
                                    ? `${formatDateShort(bookinginfo.date)} - ${formatDateShort(bookinginfo.return_date)}`
                                    : formatDateShort(bookinginfo.date)}
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowChangeFlightOverlay(true);
                                document.body.style.overflow = 'hidden';
                            }}
                            className="shrink-0 flex items-center gap-1.5 text-sm font-medium text-secondary hover:text-secondary/80 active:scale-95 active:opacity-90 transition-all duration-150"
                        >
                            <Pencil className="w-4 h-4" />
                            Change
                        </button>
                    </div>
                </div>
            )}

            {/* Change flight overlay - slides up from bottom until navbar, same flight search as main page */}
            {showChangeFlightOverlay && (
                <div className="fixed inset-0 z-[9999]">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => {
                            setShowChangeFlightOverlay(false);
                            document.body.style.overflow = '';
                        }}
                        aria-hidden="true"
                    />
                    <div className="absolute bottom-0 left-0 right-0 top-14 bg-white rounded-t-3xl shadow-2xl flex flex-col animate-slide-up">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-navy-50 to-navy-100 shrink-0">
                            <h3 className="text-lg font-bold text-gray-800">Edit Flight Search</h3>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowChangeFlightOverlay(false);
                                    document.body.style.overflow = '';
                                }}
                                className="p-2 rounded-full hover:bg-white/60 active:scale-95 active:bg-white/80 transition-all duration-150"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
                            <InputSection 
                                callby="edit_search" 
                                initialValues={bookinginfo} 
                                embeddedInOverlay 
                                onSearch={(newBookingData) => {
                                    // Close the overlay
                                    setShowChangeFlightOverlay(false);
                                    document.body.style.overflow = '';
                                    
                                    // Update bookinginfo state
                                    setBookinginfo(newBookingData);
                                    
                                    // Clear offers to force fresh search
                                    localStorage.removeItem('offer');
                                    
                                    // Trigger new search with updated parameters
                                    searchFlights(newBookingData);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Loading state - centered on screen (no modal overlay) */}
            {loadingProgress.isLoading && !showMobileBookingOverlay && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6 w-full max-w-md">
                        <div className="text-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                {loadingProgress.resultsCount > 0 
                                    ? `We have found ${loadingProgress.resultsCount} results for you so far...`
                                    : 'Searching for flights...'}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Please wait while we search for the best flights
                            </p>
                        </div>
                        <div className="mb-2">
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div 
                                    className="bg-secondary h-3 rounded-full transition-all duration-300 ease-out"
                                    style={{ width: `${loadingProgress.progressPercent}%` }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Airline strip - Compact, single row. Swipe up = hide, swipe down = show. */}
            {flights && flights.length > 0 && (
                <>
                <div 
                    className={`fixed left-0 right-0 top-[104px] z-40 w-full overflow-x-visible overflow-y-hidden bg-white transition-transform duration-300 ease-out ${
                        airlineStripVisible ? 'translate-y-0' : '-translate-y-full'
                    }`}
                >
                    <div className="relative py-1.5 px-0">
                        <div className="w-full overflow-x-auto overflow-y-hidden scrollbar-hide px-2">
                            <div 
                                className={`flex gap-2 flex-nowrap ${selectedAirlines.length > 0 ? 'pr-14' : ''}`}
                            >
                                {airlines.map((airline) => {
                                    const isSelected = selectedAirlines.includes(airline.value);
                                    return (
                                        <button
                                            type="button"
                                            key={airline.value}
                                            className={`flex-shrink-0 flex items-center gap-1.5 rounded-md border px-2 py-1.5 transition-all duration-200 ${
                                                isSelected 
                                                    ? 'border-secondary bg-navy-50' 
                                                    : 'border-gray-200 bg-gray-50 hover:border-navy-300 hover:bg-gray-100'
                                            }`}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedAirlines(selectedAirlines.filter(a => a !== airline.value));
                                                } else {
                                                    setSelectedAirlines([...selectedAirlines, airline.value]);
                                                }
                                            }}
                                            title={airline.label}
                                        >
                                            {airline.logo ? (
                                                <img 
                                                    src={airline.logo} 
                                                    alt=""
                                                    className="w-6 h-6 object-contain shrink-0"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        const next = e.target.nextElementSibling;
                                                        if (next) next.classList.remove('hidden');
                                                    }}
                                                />
                                            ) : null}
                                            <span className={`flex w-6 h-6 items-center justify-center rounded bg-gray-200 text-[10px] font-bold text-gray-500 shrink-0 ${airline.logo ? 'hidden' : ''}`}>
                                                {airline.label?.substring(0, 2).toUpperCase() || '??'}
                                            </span>
                                            <span className="text-xs font-semibold text-green-600 whitespace-nowrap">
                                                {formatCurrency(airline.cheapestPkr, 'GBP')}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {selectedAirlines.length > 0 && (
                            <button 
                                onClick={() => setSelectedAirlines([])}
                                className="absolute top-1/2 right-2 -translate-y-1/2 z-10 text-xs font-medium text-white bg-secondary hover:bg-secondary/90 rounded-full px-2.5 py-1 shadow-sm transition-colors border-0"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
                </>
            )}

            <div className={`grid grid-cols-1 md:grid-cols-4 w-full px-2 gap-y-3 gap-x-3 ${flights && flights.length > 0 ? 'mt-3 pt-[148px] md:mt-3' : 'mt-14 md:mt-0'}`}>

                <div className="hidden md:block">

                    <div className="bg-slate-100 border-none rounded-md p-3 space-y-1 shadow-md">
                        {bookinginfo?.return_date ? <p className="font-bold text-[16px]">Rouded Trip {bookinginfo?.arrival}</p> : <p className="font-bold text-[16px]"> One Way {bookinginfo?.arrival}</p>}
                        <div className="flex gap-2 items-center">
                            <p className="font-[500] text-[14px]">{bookinginfo?.departure}</p>
                            {bookinginfo?.return_date ? <ArrowRightLeft className="text-gray h-[14px]" /> : <ArrowRight className="text-gray h-[14px]" />}
                            <p className="font-[500] text-[14px]">{bookinginfo?.arrival}</p>


                        </div>
                        {bookinginfo?.return_date ? <p className="font-[500]">{formatDate(bookinginfo?.date)} - {formatDate(bookinginfo?.return_date)}</p> : <p className="font-[500]">{formatDate(bookinginfo?.date)}</p>}
                        <p className="font-[500]">{bookinginfo?.flight_class.toString().toUpperCase()}</p>
                        <button className="w-full border border-black rounded-md p-2 hover:bg-black hover:text-white" onClick={() => {
                            document.getElementById('my_modal_input_flightdetail').showModal()
                        }}>Edit Search</button>

                    </div>

                    {/* Sort By Section */}
                    <div className="mt-6">
                        <p className="font-bold text-lg tracking-[1px] mb-3 text-gray-800 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                            </svg>
                            Sort by
                        </p>
                        <div className="space-y-2">
                            {
                                sortOptions.map((x, index) => {
                                    return (
                                        <label 
                                            key={index}
                                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                                                x.value === selectedSort 
                                                    ? 'bg-navy-50 border-2 border-secondary shadow-sm' 
                                                    : 'bg-white border-2 border-gray-200 hover:border-navy-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="relative flex items-center">
                                                <input 
                                                    type="radio" 
                                                    name="sort-option"
                                                    checked={x.value === selectedSort} 
                                                    className="w-5 h-5 text-secondary cursor-pointer"
                                                    onChange={() => setSelectedSort(x.value)}
                                                />
                                            </div>
                                            <span className={`text-sm font-medium ${
                                                x.value === selectedSort ? 'text-navy-700' : 'text-gray-700'
                                            }`}>
                                                {x.label}
                                            </span>
                                        </label>
                                    )
                                })
                            }
                        </div>
                    </div>

                    {/* Airline Filter Section */}
                    <div className="mt-6">
                        <div className="flex items-center justify-between mb-3">
                            <label className="font-bold text-lg tracking-[1px] text-gray-800 flex items-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                Airlines
                            </label>
                            {selectedAirlines.length > 0 && (
                                <button 
                                    onClick={() => setSelectedAirlines([])}
                                    className="text-xs text-secondary hover:text-navy-800 font-medium transition-colors"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {
                                airlines.map((airline, index) => {
                                    const isChecked = selectedAirlines.includes(airline.value);
                                    return (
                                        <label 
                                            key={index}
                                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                                                isChecked 
                                                    ? 'bg-navy-50 border-2 border-secondary shadow-sm' 
                                                    : 'bg-white border-2 border-gray-200 hover:border-navy-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="relative flex items-center">
                                                <input 
                                                    type="checkbox" 
                                                    checked={isChecked} 
                                                    className="w-5 h-5 text-secondary cursor-pointer rounded focus:ring-2 focus:ring-secondary"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedAirlines([...selectedAirlines, airline.value]);
                                                        } else {
                                                            setSelectedAirlines(selectedAirlines.filter(a => a !== airline.value));
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="flex items-center gap-2 flex-1">
                                                {airline.logo ? (
                                                    <img 
                                                        src={airline.logo} 
                                                        alt={airline.label}
                                                        className="h-6 w-6 object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="h-6 w-6 flex items-center justify-center bg-gray-200 rounded text-xs font-bold text-gray-600">
                                                        {airline.label?.substring(0, 2).toUpperCase() || '??'}
                                                    </div>
                                                )}
                                                <span className={`text-sm font-medium ${
                                                    isChecked ? 'text-navy-700' : 'text-gray-700'
                                                }`}>
                                                    {airline.label}
                                                </span>
                                            </div>
                                        </label>
                                    )
                                })
                            }
                        </div>
                    </div>

                    {/* <div className="p-1 flex flex-col">
                        <label className="tracking-[1px] font-bold leading-6 mt-3">Flight No</label>
                        <input type="text" placeholder="Flight No" className="p-2 w-full  h-[40px] rounded-lg border border-black" value={flightNo} onChange={(e) => { setFlightNo(e.target.value) }} />
                    </div> */}





                </div>

                <div ref={flightsScrollContainerRef} className="md:col-span-3 md:max-h-[90vh] overflow-y-scroll">
                    {flights && selectedSlice ? (
                        <>
                            <FlightsDetailSection flights={flightsToShow} selectedSlice={selectedSlice} setSelectedSlice={setSelectedSlice} bookinginfo={bookinginfo} setName={setName} setPhone={setPhone} flightAlternatives={flightAlternatives} setFlightAlternatives={setFlightAlternatives} selectedFlightForBooking={selectedFlightForBooking} setSelectedFlightForBooking={setSelectedFlightForBooking} setShowAlternativesModal={setShowAlternativesModal} loading={loading} setLoading={setLoading} openBookingModal={openBookingModal} alternativesScrollYRef={alternativesScrollYRef} alternativesOriginalStylesRef={alternativesOriginalStylesRef} setAirlineStripVisible={setAirlineStripVisible} />
                            {hasMoreFlights && <div ref={loadMoreSentinelRef} className="h-4 flex-shrink-0" aria-hidden="true" />}
                        </>
                    ) : (
                        <div className="skeleton h-32 w-32"></div>
                    )}
                </div>

            </div>

            {loadingMore && (
                <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                    <div className="w-14 h-14 rounded-full border-4 border-navy-200 border-t-secondary animate-spin" role="status" aria-label="Loading more flights" />
                </div>
            )}

            <dialog id="my_modal_flightdetail" className="modal hidden md:flex items-center justify-center">
                <div className="modal-box w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <form method="dialog">
                        {!submitted && <div>
                            {/* Booking Form */}
                            <h3 className="text-lg font-bold text-gray-800 mb-3">Your Details</h3>
                            {toast && error && (
                                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-700 font-medium">{error}</p>
                                </div>
                            )}
                            <div className="p-1 flex flex-col">
                                <label className="tracking-[1px] font-[500] leading-6 ">Full Name</label>
                                <input type="text" placeholder="e.g. John Smith" value={name} className="p-3 w-full h-[40px] rounded-lg border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none" onChange={(e) => { setName(e.target.value) }} />
                            </div>
                            <div className="p-1 flex flex-col">
                                <label className="tracking-[1px] font-[500] leading-6 ">Phone Number</label>
                                <input type="tel" placeholder="07XXXXXXXXX" value={phone} maxLength={11} className="p-3 w-full h-[40px] rounded-lg border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none" onChange={handleMainPhoneChange} />
                                <span className="text-xs text-gray-500 mt-0.5">UK mobile number starting with 07 (11 digits)</span>
                            </div>
                            <div className="p-1 flex flex-col">
                                <label className="tracking-[1px] font-[500] leading-6 ">Email</label>
                                <input type="email" placeholder="e.g. john@example.com" className="p-3 w-full h-[40px] rounded-lg border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none" onChange={(e) => { setEmail(e.target.value) }} />
                            </div>

                            {/* Selected flight summary - below form, neutral background */}
                            {selectedSlice && selectedSlice.length > 0 && (
                                <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                                    {selectedSlice.map((flight, idx) => (
                                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm mb-3 last:mb-0">
                                            {/* Route */}
                                            <div className="flex items-center justify-between mb-3">
                                                {/* Left: Origin */}
                                                <div className="text-left">
                                                    <p className="text-2xl font-bold text-gray-800">{flight.origin_iatacode}</p>
                                                    <p className="text-xs text-gray-500">{flight.origin_city_name || flight.origin_name}</p>
                                                </div>
                                                
                                                {/* Center: Duration / Arrow */}
                                                <div className="flex flex-col items-center">
                                                    <ArrowRight className="text-secondary w-6 h-6" />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {flight.duration?.hours}h {flight.duration?.minutes}m
                                                    </p>
                                                </div>
                                                
                                                {/* Right: Destination */}
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-gray-800">{flight.destination_iatacode}</p>
                                                    <p className="text-xs text-gray-500">{flight.destination_city_name || flight.destination_name}</p>
                                                </div>
                                            </div>
                                            
                                            {/* Airline & Details */}
                                            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                                <div className="flex items-center gap-2">
                                                    {/* Show multiple airline logos if available */}
                                                    {flight.all_airlines && flight.all_airlines.length > 0 ? (
                                                        <div className="flex gap-1">
                                                            {flight.all_airlines.map((airline, idx) => (
                                                                <img 
                                                                    key={idx}
                                                                    src={airline.logo} 
                                                                    className="h-8 w-8 object-contain" 
                                                                    alt={airline.code}
                                                                    title={airline.code}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : flight.airline_logo ? (
                                                        <img 
                                                            src={flight.airline_logo} 
                                                            className="h-8 w-8 object-contain" 
                                                            alt={flight.airline}
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    ) : null}
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-700">
                                                            {flight.all_airlines && flight.all_airlines.length > 1 
                                                                ? `${flight.all_airlines.map(a => a.code).join(' + ')}` 
                                                                : flight.airline}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                {/* Baggage Info */}
                                                {(flight.baggage_checked || flight.baggage_cabin) && (
                                                    <div className="flex items-center gap-3 text-sm text-gray-600">
                                                        {flight.baggage_checked && (
                                                            <span className="flex items-center gap-1.5" title="Checked baggage">
                                                                <Luggage className="w-4 h-4 flex-shrink-0" />
                                                                <span className="text-xs">{flight.baggage_checked}</span>
                                                            </span>
                                                        )}
                                                        {flight.baggage_cabin && (
                                                            <span className="flex items-center gap-1.5" title="Cabin baggage">
                                                                <Briefcase className="w-4 h-4 flex-shrink-0" />
                                                                <span className="text-xs">{flight.baggage_cabin}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Branded Fare Label (if Amadeus) */}
                                            {flight.brandedFareLabel && (
                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                    <span className="inline-block px-3 py-1 bg-navy-100 text-navy-800 text-xs font-semibold rounded-full">
                                                        {flight.brandedFareLabel} - {flight.cabin}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Summary row: total passengers & total price */}
                                    <div className="mt-4 pt-4 border-t border-dashed border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                                        <span className="text-gray-700">
                                            <span className="font-semibold">Passengers:</span>{" "}
                                            {totalPassengers > 0 ? totalPassengers : 'Based on your search'}
                                        </span>
                                        <span className="text-gray-700">
                                            <span className="font-semibold">Total price:</span>{" "}
                                            <TotalFlightPriceGbp flights={selectedSlice} className="text-base font-bold text-secondary inline" />
                                        </span>
                                    </div>
                                </div>
                            )}
                            <p className="my-4 tracking-[1px] text-xs md:text-sm text-gray-600">SkyRise Travels team will contact you to confirm your travel arrangements and request payment.</p>
                            
                            <button type="button" className="btn bg-primary w-full hover:bg-navy-700 text-white font-semibold" onClick={submit}>Submit Booking Request</button>
                            <p className="my-4 tracking-[1px] text-xs md:text-sm"><span className="text-secondary font-semibold">Please Note:</span> This booking is subject to availability. This form will automatically be sent to one of our travel organizer official who will attend to this booking request during normal office hours.</p>
                        </div>}

                        {/* Close button: close modal and hide loading overlay */}
                        {!submitted && (
                            <button
                                type="button"
                                className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 "
                                onClick={closeBookingModal}
                            >
                                ✕
                            </button>
                        )}
                    </form>
                    {submitted && <h5 className="">Booking has been submitted, one of our agents will call you shortly.
                        For quick response, please call: (02081949506)</h5>}
                    {submitted && <button className="btn bg-primary w-full mt-2" onClick={() => { navigate('/') }}>Close</button>}
                </div>
            </dialog>

            {/* Mobile Booking Overlay - Bottom Sheet */}
            {showMobileBookingOverlay && (
                <div className="fixed inset-0 z-[10000] md:hidden" style={{ zIndex: 10000 }}>
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={closeBookingModal}
                    ></div>
                    
                    {/* Bottom Sheet */}
                    <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[95vh] flex flex-col animate-slide-up">
                        {/* Header with Close Button */}
                        <div className="flex items-center justify-between p-2 border-b border-gray-200 bg-gradient-to-r from-navy-50 to-navy-100 flex-shrink-0">
                            <h3 className="text-base font-bold text-gray-800">Book Your Flight</h3>
                            <button
                                onClick={closeBookingModal}
                                className="ml-2 p-1.5 rounded-full hover:bg-white/50 transition-colors"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-2 min-h-0">
                            {!submitted ? (
                                <div>
                                    {/* Booking Form */}
                                    <h3 className="text-base font-bold text-gray-800 mb-2">Your Details</h3>
                                    {toast && error && (
                                        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                            <p className="text-xs text-red-700 font-medium">{error}</p>
                                        </div>
                                    )}
                                    <div className="pb-1 flex flex-col">
                                        <label className="tracking-[1px] font-[500] leading-5 text-sm">Full Name</label>
                                        <input type="text" placeholder="e.g. John Smith" value={name} className="p-2 w-full h-[36px] rounded-lg border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none text-sm" onChange={(e) => { setName(e.target.value) }} />
                                    </div>
                                    <div className="pb-1 flex flex-col">
                                        <label className="tracking-[1px] font-[500] leading-5 text-sm">Phone Number</label>
                                        <input type="tel" placeholder="07XXXXXXXXX" value={phone} maxLength={11} className="p-2 w-full h-[36px] rounded-lg border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none text-sm" onChange={handleMainPhoneChange} />
                                        <span className="text-xs text-gray-500 mt-0.5">UK mobile starting with 07 (11 digits)</span>
                                    </div>
                                    <div className="pb-1 flex flex-col">
                                        <label className="tracking-[1px] font-[500] leading-5 text-sm">Email</label>
                                        <input type="email" placeholder="e.g. john@example.com" className="p-2 w-full h-[36px] rounded-lg border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary outline-none text-sm" onChange={(e) => { setEmail(e.target.value) }} />
                                    </div>

                                    {/* Selected flight summary - below form, neutral background */}
                                    {selectedSlice && selectedSlice.length > 0 && (
                                        <div className="mt-2 p-2 bg-white rounded-lg border border-gray-200">
                                            {selectedSlice.map((flight, idx) => (
                                                <div key={idx} className="bg-white rounded-lg p-2 shadow-sm mb-2 last:mb-0">
                                                    {/* Route */}
                                                    <div className="flex items-center justify-between mb-2">
                                                        {/* Left: Origin */}
                                                        <div className="text-left">
                                                            <p className="text-xl font-bold text-gray-800">{flight.origin_iatacode}</p>
                                                            <p className="text-xs text-gray-500">{flight.origin_city_name || flight.origin_name}</p>
                                                        </div>
                                                        
                                                        {/* Center: Duration / Arrow */}
                                                        <div className="flex flex-col items-center">
                                                            <ArrowRight className="text-secondary w-5 h-5" />
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {flight.duration?.hours}h {flight.duration?.minutes}m
                                                            </p>
                                                        </div>
                                                        
                                                        {/* Right: Destination */}
                                                        <div className="text-right">
                                                            <p className="text-xl font-bold text-gray-800">{flight.destination_iatacode}</p>
                                                            <p className="text-xs text-gray-500">{flight.destination_city_name || flight.destination_name}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Airline & Details */}
                                                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                                        <div className="flex items-center gap-2">
                                                            {/* Show multiple airline logos if available */}
                                                            {flight.all_airlines && flight.all_airlines.length > 0 ? (
                                                                <div className="flex gap-1">
                                                                    {flight.all_airlines.map((airline, idx) => (
                                                                        <img 
                                                                            key={idx}
                                                                            src={airline.logo} 
                                                                            className="h-8 w-8 object-contain" 
                                                                            alt={airline.code}
                                                                            title={airline.code}
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            ) : flight.airline_logo ? (
                                                                <img 
                                                                    src={flight.airline_logo} 
                                                                    className="h-8 w-8 object-contain" 
                                                                    alt={flight.airline}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            ) : null}
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-700">
                                                                    {flight.all_airlines && flight.all_airlines.length > 1 
                                                                        ? `${flight.all_airlines.map(a => a.code).join(' + ')}` 
                                                                        : flight.airline}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Baggage Info */}
                                                        {(flight.baggage_checked || flight.baggage_cabin) && (
                                                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                                                {flight.baggage_checked && (
                                                                    <span className="flex items-center gap-1.5" title="Checked baggage">
                                                                        <Luggage className="w-4 h-4 flex-shrink-0" />
                                                                        <span className="text-xs">{flight.baggage_checked}</span>
                                                                    </span>
                                                                )}
                                                                {flight.baggage_cabin && (
                                                                    <span className="flex items-center gap-1.5" title="Cabin baggage">
                                                                        <Briefcase className="w-4 h-4 flex-shrink-0" />
                                                                        <span className="text-xs">{flight.baggage_cabin}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Branded Fare Label (if Amadeus) */}
                                                    {flight.brandedFareLabel && (
                                                        <div className="mt-2 pt-2 border-t border-gray-200">
                                                            <span className="inline-block px-2 py-0.5 bg-navy-100 text-navy-800 text-xs font-semibold rounded-full">
                                                                {flight.brandedFareLabel} - {flight.cabin}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {/* Summary row: total passengers & total price */}
                                            <div className="mt-2 pt-2 border-t border-dashed border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs">
                                                <span className="text-gray-700">
                                                    <span className="font-semibold">Passengers:</span>{" "}
                                                    {totalPassengers > 0 ? totalPassengers : 'Based on your search'}
                                                </span>
                                                <span className="text-gray-700">
                                                    <span className="font-semibold">Total price:</span>{" "}
                                                    <TotalFlightPriceGbp flights={selectedSlice} className="text-base font-bold text-secondary inline" />
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <p className="my-2 tracking-[1px] text-xs text-gray-600">SkyRise Travels team will contact you to confirm your travel arrangements and request payment.</p>
                                    
                                    <button type="button" className="btn bg-primary w-full hover:bg-navy-700 text-white font-semibold text-sm py-2 mt-2" onClick={submit}>Submit Booking Request</button>
                                    <p className="my-2 tracking-[1px] text-xs"><span className="text-secondary font-semibold">Please Note:</span> This booking is subject to availability. This form will automatically be sent to one of our travel organizer official who will attend to this booking request during normal office hours.</p>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <h5 className="mb-2 text-sm">Booking has been submitted, one of our agents will call you shortly.
                                        For quick response, please call: (02081949506)</h5>
                                    <button className="btn bg-primary w-full mt-2 text-sm py-2" onClick={() => { 
                                        closeBookingModal();
                                        navigate('/');
                                    }}>Close</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <dialog id="my_modal_input_flightdetail" className="modal backdrop-blur-sm z-[10000]">
                <div className="modal-box w-11/12 max-w-4xl p-0 overflow-visible bg-gradient-to-br from-navy-50 via-white to-navy-50">
                    <form method="dialog">
                        <button 
                            className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 z-10 hover:bg-navy-100 hover:text-secondary transition-colors" 
                            onClick={() => {
                                document.getElementById('my_modal_input_flightdetail')?.close()
                            }}
                        >
                            ✕
                        </button>
                    </form>

                    {/* Beautiful Header */}
                    <div className="bg-gradient-to-r from-secondary to-navy-800 px-6 py-5 text-white">
                        <h3 className="text-2xl font-bold mb-1">Edit Flight Search</h3>
                        <p className="text-navy-100 text-sm">Modify your search criteria below</p>
                    </div>

                    {/* Scrollable Content */}
                    <div className="max-h-[80vh] overflow-y-auto px-6 py-6">
                        <InputSection callby={`edit_search`} initialValues={bookinginfo} />
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            {/* Discount Dialog */}
            {showDiscountDialog && (
                <div 
                    className="fixed inset-0 z-[9999] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={handleBackdropClick}
                >
                    <div 
                        className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-slide-up"
                        onClick={handleDialogContentClick}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-center p-6 border-b border-gray-200 bg-secondary text-white rounded-t-2xl">
                            <h3 className="text-xl font-bold">Unlock Discounted Fare</h3>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <p className="text-gray-700 mb-6 text-center">
                                Apply Discount Code to unlock exclusive fares on your flights up to 20% OFF
                            </p>

                            {/* Error Message */}
                            {dialogError && (
                                <div className="mb-4 p-3 bg-navy-50 border border-navy-200 rounded-lg">
                                    <p className="text-sm text-secondary">{dialogError}</p>
                                </div>
                            )}

                            {/* Name Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    value={dialogName}
                                    onChange={(e) => setDialogName(e.target.value)}
                                    placeholder="Enter your full name"
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary text-base"
                                />
                            </div>

                            {/* Phone Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    value={dialogPhone}
                                    onChange={handleDialogPhoneChange}
                                    placeholder="07XXXXXXXXX"
                                    maxLength={11}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary text-base"
                                />
                                <p className="text-xs text-gray-500 mt-1">UK mobile number starting with 07 (11 digits)</p>
                            </div>

                            {/* Discount Code Applied */}
                            <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-semibold text-green-800">Discount Code Applied</p>
                                        <p className="text-xs text-green-600">Code: <span className="font-mono font-bold text-secondary">BUDGET2026</span></p>
                                    </div>
                                </div>
                            </div>

                            {/* Apply Button */}
                            <button
                                onClick={handleUnlockDiscount}
                                className="w-full bg-secondary hover:bg-navy-700 text-white font-bold py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99]"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alternatives Modal */}
            {showAlternativesModal && selectedFlightForBooking && (
                <>
                    {/* Desktop Modal */}
                    <div 
                        className="hidden md:flex fixed inset-0 z-[9999] bg-black bg-opacity-50 backdrop-blur-sm items-center justify-center p-4"
                        onClick={closeAlternativesModal}
                    >
                        <div 
                            className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-secondary to-navy-800 text-white rounded-t-2xl">
                                <div>
                                    <h3 className="text-xl font-bold">Choose Your Fare Option</h3>
                                    <p className="text-sm text-navy-100 mt-1">Compare branded fares and baggage allowances</p>
                                </div>
                                <button 
                                    onClick={closeAlternativesModal}
                                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary mb-4"></div>
                                    <p className="text-gray-600">Loading fare options...</p>
                                </div>
                            ) : (
                            <div className="space-y-4">
                                {(selectedFlightForBooking.flightKey || selectedFlightForBooking.id) && 
                                 flightAlternatives[selectedFlightForBooking.flightKey || selectedFlightForBooking.id]?.map((altFlight, idx) => {
                                    const isCurrentFlight = altFlight.id === selectedFlightForBooking.id;
                                    const priceDiff = parseFloat(altFlight.amount) - parseFloat(selectedFlightForBooking.amount);
                                    
                                    return (
                                        <div 
                                            key={idx} 
                                            className={`border-2 rounded-xl p-5 transition-all hover:shadow-lg cursor-pointer ${
                                                isCurrentFlight 
                                                    ? 'border-secondary bg-navy-50' 
                                                    : 'border-gray-200 hover:border-navy-300'
                                            }`}
                                            onClick={() => {
                                                // Proceed with booking this flight
                                                const bookingData = localStorage.getItem('booking');
                                                if (bookingData) {
                                                    try {
                                                        const booking = JSON.parse(bookingData);
                                                        if (booking.name) setName(booking.name);
                                                        if (booking.phone) setPhone(booking.phone);
                                                    } catch (error) {
                                                        console.error('Error loading booking data:', error);
                                                    }
                                                }
                                                // Replace selectedSlice with the selected alternative flight
                                                setSelectedSlice([altFlight]);
                                                closeAlternativesModal();
                                                openBookingModal();
                                            }}
                                        >
                                            {/* Fare Class Header */}
                                            <div className="mb-4">
                                                <h4 className="text-xl font-bold text-gray-900 mb-1">
                                                    {altFlight.brandedFareLabel || 'Standard Fare'}
                                                </h4>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="px-2 py-1 bg-navy-100 text-navy-800 text-xs font-semibold rounded">
                                                        {altFlight.cabin || 'ECONOMY'}
                                                    </span>
                                                    {isCurrentFlight && (
                                                        <span className="px-2 py-1 bg-navy-500 text-white text-xs font-bold rounded">
                                                            CURRENT SELECTION
                                                        </span>
                                                    )}
                                                    {!isCurrentFlight && priceDiff < 0 && (
                                                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">
                                                            SAVE <PriceDisplay amount={Math.abs(priceDiff)} displayCurrency="GBP" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <FlightPriceGbp
                                                            amount={altFlight.amount}
                                                            sourceCurrency={altFlight.currency || 'GBP'}
                                                            className="text-2xl font-bold text-gray-800"
                                                        />
                                                    </div>
                                                    {bookinginfo && (
                                                        <p className="text-xs text-gray-500">
                                                            {parseInt(bookinginfo.adults || '1')}x Adult{parseInt(bookinginfo.adults || '1') > 1 ? 's' : ''}
                                                        </p>
                                                    )}
                                                </div>
                                                <button 
                                                    className="px-6 py-2 bg-secondary hover:bg-navy-700 text-white font-semibold rounded-lg transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Trigger the parent onClick
                                                        e.currentTarget.parentElement.parentElement.click();
                                                    }}
                                                >
                                                    Select
                                                </button>
                                            </div>

                                            {/* Flexibility Features */}
                                            <div className="grid grid-cols-2 gap-3 mb-4">
                                                <div className={`p-3 rounded-lg border ${altFlight.isRefundable ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-2">
                                                        {altFlight.isRefundable ? (
                                                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        )}
                                                        <div>
                                                            <p className={`text-xs font-semibold ${altFlight.isRefundable ? 'text-green-700' : 'text-gray-600'}`}>
                                                                Refundable
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {altFlight.isRefundable ? 'Yes' : 'No'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className={`p-3 rounded-lg border ${altFlight.isChangeable ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-2">
                                                        {altFlight.isChangeable ? (
                                                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        )}
                                                        <div>
                                                            <p className={`text-xs font-semibold ${altFlight.isChangeable ? 'text-green-700' : 'text-gray-600'}`}>
                                                                Changeable
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {altFlight.hasChangeFee ? 'Fee applies' : altFlight.isChangeable ? 'Free' : 'No'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Full Flexibility Badge */}
                                            {altFlight.hasFullFlexibility && (
                                                <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-navy-50 border border-purple-200 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                        </svg>
                                                        <div>
                                                            <p className="text-sm font-bold text-purple-700">Full Flexibility</p>
                                                            <p className="text-xs text-purple-600">Change or cancel anytime without penalties</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Baggage Info */}
                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                {(altFlight.baggage_checked || altFlight.baggage_cabin) ? (
                                                    <div className="flex items-center gap-4 text-sm text-gray-700 flex-wrap">
                                                        {altFlight.baggage_checked && (
                                                            <span className="flex items-center gap-2" title="Checked baggage">
                                                                <Luggage className="w-5 h-5 text-secondary flex-shrink-0" />
                                                                <span>{altFlight.baggage_checked}</span>
                                                            </span>
                                                        )}
                                                        {altFlight.baggage_cabin && (
                                                            <span className="flex items-center gap-2" title="Cabin baggage">
                                                                <Briefcase className="w-5 h-5 text-secondary flex-shrink-0" />
                                                                <span>{altFlight.baggage_cabin}</span>
                                                            </span>
                                                        )}
                                                        <span className="text-xs text-gray-500 italic">Per passenger</span>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-gray-600">No baggage included</p>
                                                )}
                                            </div>

                                            {/* Price Comparison */}
                                            {!isCurrentFlight && (
                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                    {priceDiff > 0 ? (
                                                        <p className="text-sm text-orange-600 font-medium">
                                                            +<PriceDisplay amount={priceDiff} displayCurrency="GBP" /> more than your selection
                                                        </p>
                                                    ) : priceDiff < 0 ? (
                                                        <p className="text-sm text-green-600 font-medium">
                                                            Save <PriceDisplay amount={Math.abs(priceDiff)} displayCurrency="GBP" /> compared to your selection
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-gray-600">Same price as your selection</p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Availability Warning */}
                                            {altFlight.numberOfBookableSeats <= 3 && altFlight.numberOfBookableSeats > 0 && (
                                                <div className="mt-3 flex items-center gap-2 text-orange-600">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    <p className="text-xs font-medium">Only {altFlight.numberOfBookableSeats} seat(s) left!</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            )}

                            {/* Footer Note */}
                            {!loading && (
                                <div className="mt-6 p-4 bg-navy-50 rounded-lg border border-navy-100">
                                    <p className="text-sm text-gray-700">
                                        <strong>Note:</strong> All fares are for the same flight with different branded fare options and baggage allowances. Select the option that best suits your needs.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Bottom Sheet */}
                <div className="md:hidden fixed inset-0 z-[10000]" style={{ zIndex: 10000 }}>
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={closeAlternativesModal}
                    ></div>
                    
                    {/* Bottom Sheet */}
                    <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[95vh] flex flex-col animate-slide-up">
                        {/* Header with Close Button */}
                        <div className="flex items-center justify-between p-2 border-b border-gray-200 bg-gradient-to-r from-secondary to-navy-800 text-white flex-shrink-0">
                            <div>
                                <h3 className="text-base font-bold">Choose Your Fare Option</h3>
                                <p className="text-xs text-navy-100 mt-0.5">Compare branded fares and baggage allowances</p>
                            </div>
                            <button
                                onClick={closeAlternativesModal}
                                className="ml-2 p-1.5 rounded-full hover:bg-white/20 transition-colors"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-2 min-h-0">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary mb-3"></div>
                                    <p className="text-sm text-gray-600">Loading fare options...</p>
                                </div>
                            ) : (
                            <div className="space-y-2">
                                {(selectedFlightForBooking.flightKey || selectedFlightForBooking.id) && 
                                 flightAlternatives[selectedFlightForBooking.flightKey || selectedFlightForBooking.id]?.map((altFlight, idx) => {
                                    const isCurrentFlight = altFlight.id === selectedFlightForBooking.id;
                                    const priceDiff = parseFloat(altFlight.amount) - parseFloat(selectedFlightForBooking.amount);
                                    
                                    return (
                                        <div 
                                            key={idx} 
                                            className={`border-2 rounded-lg p-3 transition-all cursor-pointer ${
                                                isCurrentFlight 
                                                    ? 'border-secondary bg-navy-50' 
                                                    : 'border-gray-200'
                                            }`}
                                            onClick={() => {
                                                // Restore scroll before closing
                                                const original = alternativesOriginalStylesRef.current;
                                                if (original.position !== undefined) {
                                                    document.body.style.position = original.position || '';
                                                    document.body.style.top = original.top || '';
                                                    document.body.style.width = original.width || '';
                                                    document.body.style.overflow = original.overflow || '';
                                                    document.documentElement.style.overflow = original.htmlOverflow || '';
                                                    
                                                    // Restore scroll position
                                                    const scrollY = alternativesScrollYRef.current;
                                                    requestAnimationFrame(() => {
                                                        window.scrollTo(0, scrollY);
                                                    });
                                                    
                                                    // Clear refs
                                                    alternativesOriginalStylesRef.current = {};
                                                }
                                                
                                                // Proceed with booking this flight
                                                const bookingData = localStorage.getItem('booking');
                                                if (bookingData) {
                                                    try {
                                                        const booking = JSON.parse(bookingData);
                                                        if (booking.name) setName(booking.name);
                                                        if (booking.phone) setPhone(booking.phone);
                                                    } catch (error) {
                                                        console.error('Error loading booking data:', error);
                                                    }
                                                }
                                                // Replace selectedSlice with the selected alternative flight
                                                setSelectedSlice([altFlight]);
                                                closeAlternativesModal();
                                                openBookingModal();
                                            }}
                                        >
                                            {/* Fare Class Header */}
                                            <div className="mb-2">
                                                <h4 className="text-base font-bold text-gray-900 mb-1">
                                                    {altFlight.brandedFareLabel || 'Standard Fare'}
                                                </h4>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="px-2 py-0.5 bg-navy-100 text-navy-800 text-xs font-semibold rounded">
                                                        {altFlight.cabin || 'ECONOMY'}
                                                    </span>
                                                    {isCurrentFlight && (
                                                        <span className="px-2 py-0.5 bg-navy-500 text-white text-xs font-bold rounded">
                                                            CURRENT
                                                        </span>
                                                    )}
                                                    {!isCurrentFlight && priceDiff < 0 && (
                                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">
                                                            SAVE <PriceDisplay amount={Math.abs(priceDiff)} displayCurrency="GBP" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <FlightPriceGbp
                                                            amount={altFlight.amount}
                                                            sourceCurrency={altFlight.currency || 'GBP'}
                                                            className="text-xl font-bold text-gray-800"
                                                        />
                                                    </div>
                                                    {bookinginfo && (
                                                        <p className="text-xs text-gray-500">
                                                            {parseInt(bookinginfo.adults || '1')}x Adult{parseInt(bookinginfo.adults || '1') > 1 ? 's' : ''}
                                                        </p>
                                                    )}
                                                </div>
                                                <button 
                                                    className="px-4 py-1.5 bg-secondary hover:bg-navy-700 text-white font-semibold rounded-lg transition-colors text-sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.currentTarget.parentElement.parentElement.click();
                                                    }}
                                                >
                                                    Select
                                                </button>
                                            </div>

                                            {/* Flexibility Features */}
                                            <div className="grid grid-cols-2 gap-2 mb-2">
                                                <div className={`p-2 rounded-lg border text-xs ${altFlight.isRefundable ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-1.5">
                                                        {altFlight.isRefundable ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        )}
                                                        <div>
                                                            <p className={`text-xs font-semibold ${altFlight.isRefundable ? 'text-green-700' : 'text-gray-600'}`}>
                                                                Refundable
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {altFlight.isRefundable ? 'Yes' : 'No'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className={`p-2 rounded-lg border text-xs ${altFlight.isChangeable ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-1.5">
                                                        {altFlight.isChangeable ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        )}
                                                        <div>
                                                            <p className={`text-xs font-semibold ${altFlight.isChangeable ? 'text-green-700' : 'text-gray-600'}`}>
                                                                Changeable
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {altFlight.hasChangeFee ? 'Fee applies' : altFlight.isChangeable ? 'Free' : 'No'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Full Flexibility Badge */}
                                            {altFlight.hasFullFlexibility && (
                                                <div className="mb-2 p-2 bg-gradient-to-r from-purple-50 to-navy-50 border border-purple-200 rounded-lg">
                                                    <div className="flex items-center gap-1.5">
                                                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                        </svg>
                                                        <div>
                                                            <p className="text-xs font-bold text-purple-700">Full Flexibility</p>
                                                            <p className="text-xs text-purple-600">Change or cancel anytime</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Baggage Info */}
                                            <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                                                {(altFlight.baggage_checked || altFlight.baggage_cabin) ? (
                                                    <div className="flex items-center gap-2 text-xs text-gray-700 flex-wrap">
                                                        {altFlight.baggage_checked && (
                                                            <span className="flex items-center gap-1.5" title="Checked baggage">
                                                                <Luggage className="w-4 h-4 text-secondary flex-shrink-0" />
                                                                <span>{altFlight.baggage_checked}</span>
                                                            </span>
                                                        )}
                                                        {altFlight.baggage_cabin && (
                                                            <span className="flex items-center gap-1.5" title="Cabin baggage">
                                                                <Briefcase className="w-4 h-4 text-secondary flex-shrink-0" />
                                                                <span>{altFlight.baggage_cabin}</span>
                                                            </span>
                                                        )}
                                                        <span className="text-xs text-gray-500 italic">Per passenger</span>
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-gray-600">No baggage included</p>
                                                )}
                                            </div>

                                            {/* Price Comparison */}
                                            {!isCurrentFlight && (
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    {priceDiff > 0 ? (
                                                        <p className="text-xs text-orange-600 font-medium">
                                                            +<PriceDisplay amount={priceDiff} displayCurrency="GBP" /> more than your selection
                                                        </p>
                                                    ) : priceDiff < 0 ? (
                                                        <p className="text-xs text-green-600 font-medium">
                                                            Save <PriceDisplay amount={Math.abs(priceDiff)} displayCurrency="GBP" /> compared to your selection
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs text-gray-600">Same price as your selection</p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Availability Warning */}
                                            {altFlight.numberOfBookableSeats <= 3 && altFlight.numberOfBookableSeats > 0 && (
                                                <div className="mt-2 flex items-center gap-1.5 text-orange-600">
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    <p className="text-xs font-medium">Only {altFlight.numberOfBookableSeats} seat(s) left!</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            )}

                            {/* Footer Note */}
                            {!loading && (
                                <div className="mt-3 p-2 bg-navy-50 rounded-lg border border-navy-100">
                                    <p className="text-xs text-gray-700">
                                        <strong>Note:</strong> All fares are for the same flight with different branded fare options and baggage allowances. Select the option that best suits your needs.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                </>
            )}
        </div>
    );
};


const FlightsDetailSection = ({ 
    flights, 
    selectedSlice, 
    bookinginfo, 
    setSelectedSlice, 
    setName, 
    setPhone,
    flightAlternatives = {},
    setFlightAlternatives = () => {},
    selectedFlightForBooking = null,
    setSelectedFlightForBooking = () => {},
    setShowAlternativesModal = () => {},
    loading = false,
    setLoading = () => {},
    openBookingModal = () => {},
    alternativesScrollYRef,
    alternativesOriginalStylesRef,
    setAirlineStripVisible = () => {}
}) => {

    const [activeIndex, setActiveIndex] = useState(null);
    // console.log(bookinginfo, selectedSlice) // disabled to reduce console noise
    
    // Helper function to format date - ensures correct date display regardless of timezone
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            // Extract date part from ISO string to avoid timezone conversion
            // Handle both "2025-12-16" and "2025-12-16T19:00:00.000Z" formats
            let datePart = dateString;
            if (dateString.includes('T')) {
                datePart = dateString.split('T')[0];
            }
            
            // Remove any timezone info if present
            datePart = datePart.trim();
            
            // Parse the date part (YYYY-MM-DD format)
            const parts = datePart.split('-');
            if (parts.length !== 3) {
                throw new Error('Invalid date format');
            }
            
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            
            // Validate the date values
            if (isNaN(year) || isNaN(month) || isNaN(day)) {
                throw new Error('Invalid date values');
            }
            
            // Create date using UTC at noon to avoid day boundary issues
            const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            
            // Format using UTC timezone to ensure correct date display
            return utcDate.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric',
                timeZone: 'UTC'
            });
        } catch (error) {
            // Fallback: try to parse as-is and format using UTC
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return dateString; // Return original if invalid
                }
                // Extract date components using UTC to avoid timezone issues
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth();
                const day = date.getUTCDate();
                const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
                return utcDate.toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric',
                    timeZone: 'UTC'
                });
            } catch (e) {
                return dateString; // Return original if all parsing fails
            }
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const formatDateWithDay = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            let datePart = dateString;
            if (dateString.includes('T')) datePart = dateString.split('T')[0];
            datePart = datePart.trim();
            const parts = datePart.split('-');
            if (parts.length !== 3) return formatDate(dateString);
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            if (isNaN(year) || isNaN(month) || isNaN(day)) return formatDate(dateString);
            const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            const weekday = utcDate.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
            const rest = utcDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
            return `${weekday}, ${rest}`;
        } catch {
            return formatDate(dateString);
        }
    };
    
    return (
        flights ? flights.filter(f => f.amount && parseFloat(f.amount) > 0).map((f, index) => {
            return (<div key={f.id || `flight_${f.id2 ?? index}`} className="grid grid-cols-3 p-3 border border-mLightGray rounded-lg shadow-lg mb-3">

                <div className={`col-span-3 flex w-full ${bookinginfo?.return_date ? 'flex-row justify-between items-start gap-2 sm:gap-4 md:gap-6' : ''}`}>
                    {/* Going flight - left */}
                    <div className={`flex flex-col gap-0 ${bookinginfo?.return_date ? 'flex-1 min-w-0 max-w-[50%]' : ''}`}>
                        {(() => {
                            const segs = f.origin_segments || [];
                            const first = segs[0];
                            const last = segs[segs.length - 1];
                            const depTime = first && getSegmentDepTime(first);
                            const arrTime = last && getSegmentArrTime(last);
                            const depDate = depTime && (String(depTime).includes('T') ? depTime.split('T')[0] : String(depTime).slice(0, 10));
                            const arrDate = arrTime && (String(arrTime).includes('T') ? arrTime.split('T')[0] : String(arrTime).slice(0, 10));
                            let isNextDay = false;
                            if (depDate && arrDate && /^\d{4}-\d{2}-\d{2}$/.test(depDate) && /^\d{4}-\d{2}-\d{2}$/.test(arrDate)) {
                                const d = new Date(depDate + 'T12:00:00Z');
                                d.setUTCDate(d.getUTCDate() + 1);
                                const nextDayStr = d.toISOString().slice(0, 10);
                                isNextDay = arrDate === nextDayStr;
                            }
                            return (
                                <>
                                    <div className="flex items-start gap-2">
                                        <div className="flex flex-col">
                                            <p className="font-bold text-lg">{f.origin_iatacode}</p>
                                            {depTime ? <p className="text-md font-bold">{formatTime(depTime)}</p> : null}
                                        </div>
                                        <div className="flex items-center justify-center self-center shrink-0 pt-1">
                                            <ArrowRight className="text-gray h-[14px]" />
                                        </div>
                                        <div className="flex flex-col">
                                            <p className="font-bold text-lg">{f.destination_iatacode}</p>
                                            {arrTime ? (
                                                <p className="text-md font-bold inline-flex items-baseline gap-0.5">
                                                    {formatTime(arrTime)}
                                                    {isNextDay && <span className="text-secondary text-xs font-bold">+1</span>}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                    <p className="text-md mt-1">{formatDateWithDay(bookinginfo?.date)}</p>
                                </>
                            );
                        })()}
                    </div>

                    {/* Arrows image separator for return flights */}
                    {bookinginfo?.return_date && (
                        <div className="flex items-center justify-center shrink-0 px-2 mt-4">
                            <img src={arrowsLeftRight} alt="Round trip" className="h-5 w-auto object-contain" />
                        </div>
                    )}

                    {/* Return flight - right, opposite departure, same layout as going */}
                    {bookinginfo?.return_date ? (
                        <div className="flex flex-col gap-0 flex-1 min-w-0 max-w-[50%]">
                            {(() => {
                                const segs = f.return_segments || f.return_origin_segments || [];
                                const first = segs[0];
                                const last = segs[segs.length - 1];
                                const depTime = first && getSegmentDepTime(first);
                                const arrTime = last && getSegmentArrTime(last);
                                const depDate = depTime && (String(depTime).includes('T') ? depTime.split('T')[0] : String(depTime).slice(0, 10));
                                const arrDate = arrTime && (String(arrTime).includes('T') ? arrTime.split('T')[0] : String(arrTime).slice(0, 10));
                                let isNextDay = false;
                                if (depDate && arrDate && /^\d{4}-\d{2}-\d{2}$/.test(depDate) && /^\d{4}-\d{2}-\d{2}$/.test(arrDate)) {
                                    const d = new Date(depDate + 'T12:00:00Z');
                                    d.setUTCDate(d.getUTCDate() + 1);
                                    const nextDayStr = d.toISOString().slice(0, 10);
                                    isNextDay = arrDate === nextDayStr;
                                }
                                return (
                                    <>
                                        <div className="flex items-start justify-end gap-2">
                                            <div className="flex flex-col">
                                                <p className="font-bold text-lg">{f.destination_iatacode}</p>
                                                {depTime ? <p className="text-md font-bold">{formatTime(depTime)}</p> : null}
                                            </div>
                                            <div className="flex items-center justify-center self-center shrink-0 pt-1">
                                                <ArrowRight className="text-gray h-[14px]" />
                                            </div>
                                            <div className="flex flex-col">
                                                <p className="font-bold text-lg">{f.origin_iatacode}</p>
                                                {arrTime ? (
                                                    <p className="text-md font-bold inline-flex items-baseline gap-0.5">
                                                        {formatTime(arrTime)}
                                                        {isNextDay && <span className="text-secondary text-xs font-bold">+1</span>}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>
                                        <p className="text-md mt-1 text-right">{formatDateWithDay(bookinginfo?.return_date)}</p>
                                    </>
                                );
                            })()}
                        </div>
                    ) : null}
                </div>

                <div className="col-span-3 flex gap-2 items-center ">
                </div>
                <div className="col-span-full my-2">
                    <hr className="text-gray" />
                </div>

                <div className="flex flex-col ">
                    <p className="font-bold">AirLine</p>
                    <div className="flex gap-2 items-center">
                        {/* Show multiple airline logos if available */}
                        {f.all_airlines && f.all_airlines.length > 0 ? (
                            <div className="flex gap-2">
                                {f.all_airlines.map((airline, idx) => (
                                    <img 
                                        key={idx}
                                        src={airline.logo} 
                                        className="h-[40px] w-[40px] object-contain" 
                                        alt={airline.code}
                                        title={airline.code}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ))}
                            </div>
                        ) : f.airline_logo ? (
                            <img 
                                src={f.airline_logo} 
                                className="h-[40px] w-[40px] object-contain" 
                                alt={f.airline}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <div className="h-[40px] w-[40px] flex items-center justify-center bg-gray-200 rounded text-xs font-bold text-gray-600">
                                {f.airline?.substring(0, 2).toUpperCase() || '??'}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-center">
                    <p className="font-bold">Duration</p>
                    <div className="flex gap-2 items-center">
                        <p className="mt-2">{f.duration.hours}h{f.duration.minutes}m</p>
                    </div>
                </div>
                <div className="flex flex-col md:justify-end items-center md:items-end mt-2 md:mt-0 gap-2">

                    <button 
                        type="button"
                        className="rounded-md bg-primary py-2 px-3 text-sm font-[500] tracking-[1px] text-navy-700 hover:bg-navy-700 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto" 
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Load name and phone from localStorage if available
                            const bookingData = localStorage.getItem('booking');
                            if (bookingData) {
                                try {
                                    const booking = JSON.parse(bookingData);
                                    if (booking.name) setName(booking.name);
                                    if (booking.phone) setPhone(booking.phone);
                                } catch (error) {
                                    console.error('Error loading booking data:', error);
                                }
                            }
                            // Replace selectedSlice with only the current flight
                            setSelectedSlice([f]);
                            // Use openBookingModal to handle mobile/desktop
                            openBookingModal();
                        }}
                    >
                        Book Now
                    </button>
                    
                    {/* More Options Button - Only show for Amadeus flights */}
                    {f.source === 'Amadeus' && f.rawAmadeusOffer && (
                        <button 
                            className="text-sm text-secondary hover:text-navy-800 font-medium underline transition-colors w-full md:w-auto flex items-center gap-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading && selectedFlightForBooking?.id === f.id}
                            onClick={async (e) => {
                                e.stopPropagation();
                                console.log('🔍 Exploring fare options for Amadeus flight:', f.id);
                                // Keep airline strip visible immediately
                                setAirlineStripVisible(true);
                                setSelectedFlightForBooking(f);
                                setLoading(true);
                                
                                try {
                                    const response = await service.postGetBrandedFares({ 
                                        flightOffer: f.rawAmadeusOffer 
                                    });
                                    
                                    console.log('✅ Branded fares received:', response.data);
                                    
                                    if (response.data?.brandedFares && response.data.brandedFares.length >= 1) {
                                        // Show fare options modal
                                        const alternatives = {};
                                        alternatives[f.flightKey || f.id] = response.data.brandedFares.map((fare, idx) => {
                                            // Convert to our flight object format
                                            const segments = fare.itineraries?.[0]?.segments || [];
                                            const firstSegment = segments[0] || {};
                                            const fareDetails = fare.travelerPricings?.[0]?.fareDetailsBySegment?.[0] || {};
                                            
                                            // Extract baggage info
                                            const checkedBags = fareDetails.includedCheckedBags;
                                            const cabinBags = fareDetails.includedCabinBags;
                                            let baggageChecked = null;
                                            let baggageCabin = null;
                                            if (checkedBags) {
                                                baggageChecked = checkedBags.weight
                                                    ? `${checkedBags.weight}${checkedBags.weightUnit || 'KG'}`
                                                    : (checkedBags.quantity ? String(checkedBags.quantity) : null);
                                            }
                                            if (cabinBags) {
                                                baggageCabin = cabinBags.weight
                                                    ? `${cabinBags.weight}${cabinBags.weightUnit || 'KG'}`
                                                    : (cabinBags.quantity ? String(cabinBags.quantity) : null);
                                            }
                                            const baggageParts = [];
                                            if (baggageChecked) baggageParts.push(`${baggageChecked} checked`);
                                            if (baggageCabin) baggageParts.push(`${baggageCabin} cabin`);
                                            const baggageInfo = baggageParts.join(', ');
                                            
                                            // Extract fare class and flexibility info
                                            const brandedFareLabel = fareDetails.brandedFareLabel || fareDetails.brandedFare || 'Standard';
                                            const cabin = fareDetails.cabin || 'ECONOMY';
                                            
                                            // Check pricing options for refundability and flexibility
                                            const pricingOptions = fare.pricingOptions || {};
                                            const isRefundable = pricingOptions.refundableFare || false;
                                            const noRestriction = pricingOptions.noRestrictionFare || false;
                                            const noPenalty = pricingOptions.noPenaltyFare || false;
                                            
                                            // Extract amenities to check for change/refund details
                                            const amenities = fareDetails.amenities || [];
                                            const changeableTicket = amenities.find(a => 
                                                a.description?.toLowerCase().includes('changeable') || 
                                                a.amenityType === 'BRANDED_FARES' && a.description?.toLowerCase().includes('change')
                                            );
                                            const refundableTicket = amenities.find(a => 
                                                a.description?.toLowerCase().includes('refund')
                                            );
                                            const changeFeeAmenity = amenities.find(a => 
                                                a.description?.toLowerCase().includes('change fee')
                                            );
                                            
                                            return {
                                                ...f,
                                                id: fare.id || `branded_${idx}`,
                                                amount: fare.price?.total || fare.price?.grandTotal || f.amount,
                                                baggage: baggageInfo,
                                                baggage_checked: baggageChecked,
                                                baggage_cabin: baggageCabin,
                                                brandedFareLabel: brandedFareLabel,
                                                cabin: cabin,
                                                isRefundable: isRefundable || (refundableTicket && !refundableTicket.isChargeable),
                                                isChangeable: noRestriction || noPenalty || (changeableTicket && !changeableTicket.isChargeable),
                                                hasChangeFee: changeFeeAmenity?.isChargeable || false,
                                                hasFullFlexibility: noRestriction || noPenalty,
                                                amenities: amenities,
                                                rawAmadeusOffer: fare,
                                                numberOfBookableSeats: fare.numberOfBookableSeats || 0
                                            };
                                        });
                                        
                                        setFlightAlternatives(alternatives);
                                        setLoading(false);
                                        
                                        // Lock scroll for mobile before opening modal
                                        const checkMobileNow = window.innerWidth <= 768;
                                        if (checkMobileNow) {
                                            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                                            alternativesScrollYRef.current = scrollY;
                                            
                                            // Store original styles
                                            alternativesOriginalStylesRef.current = {
                                                position: document.body.style.position,
                                                top: document.body.style.top,
                                                width: document.body.style.width,
                                                overflow: document.body.style.overflow,
                                                htmlOverflow: document.documentElement.style.overflow
                                            };
                                            
                                            // Lock scroll position IMMEDIATELY using fixed positioning
                                            document.body.style.position = 'fixed';
                                            document.body.style.top = `-${scrollY}px`;
                                            document.body.style.width = '100%';
                                            document.body.style.overflow = 'hidden';
                                            document.documentElement.style.overflow = 'hidden';
                                            
                                            // Push history state when opening modal
                                            window.history.pushState({ overlayOpen: 'alternatives' }, '');
                                        }
                                        
                                        setShowAlternativesModal(true);
                                    } else {
                                        console.log('ℹ️ No fare options available');
                                        setLoading(false);
                                        alert('No additional fare options available for this flight.');
                                    }
                                } catch (error) {
                                    console.error('Error fetching branded fares:', error);
                                    setLoading(false);
                                    alert('Unable to load fare options. Please try again.');
                                }
                            }}
                        >
                            {loading && selectedFlightForBooking?.id === f.id ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-secondary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Loading...</span>
                                </>
                            ) : (
                                'More options'
                            )}
                        </button>
                    )}
                </div>

                <div className="col-span-full">
                    <AccordionItem
                        key={index}
                        title={`Total Fare`}
                        content={f.amount}
                        index={index}
                        activeIndex={activeIndex}
                        setActiveIndex={setActiveIndex}
                        flight={f}
                        showReturn={bookinginfo.return_date && selectedSlice.find(a => a == f.id2)}
                        bookinginfo={bookinginfo}
                        flightAlternatives={flightAlternatives}
                    />
                </div>

                <div className="col-span-full flex flex-col justify-center items-center mt-2">
                    <p className="text-md md:text-lg  tracking-[1px] text-center">Special prices</p>
                    
                    <div className="flex gap-4 items-center flex-wrap justify-center">
                        {/* Call Us Button */}
                        <div className="flex gap-2 items-center cursor-pointer" role="button" onClick={() => {
                            window.location.href = 'tel:02081949506'
                        }}>
                            <PhoneCall className="text-navy-700" />
                            <p className="text-md md:text-lg font-semibold tracking-[1px] text-navy-700">Call Us Now</p>
                        </div>

                        {/* OR Text */}
                        <span className="text-md md:text-lg font-semibold tracking-[1px] text-gray-600">OR</span>

                        {/* WhatsApp Button */}
                        <div className="flex gap-2 items-center cursor-pointer" role="button" onClick={() => {
                            // Format departure date
                            const departureDate = bookinginfo?.date ? new Date(bookinginfo.date).toLocaleDateString('en-GB', { 
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric' 
                            }) : '';
                            
                            // Format return date if exists
                            const returnDate = bookinginfo?.return_date ? new Date(bookinginfo.return_date).toLocaleDateString('en-GB', { 
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric' 
                            }) : '';
                            
                            const flightType = bookinginfo?.return_date ? 'Return' : 'One-Way';
                            
                            // Create WhatsApp message with flight details
                            let message = `Hello, I want to book this flight:%0A%0A` +
                                `✈️ *Flight Details*%0A` +
                                `━━━━━━━━━━━━━━━━%0A` +
                                `🎫 Type: ${flightType}%0A` +
                                `📍 From: ${f.origin_city_name} (${f.origin_iatacode})%0A` +
                                `📍 To: ${f.destination_city_name} (${f.destination_iatacode})%0A` +
                                `📅 Departure: ${departureDate}%0A`;
                            
                            if (returnDate) {
                                message += `📅 Return: ${returnDate}%0A`;
                            }
                            
                            message += `✈️ Airline: ${f.airline}%0A` +
                                `⏱️ Duration: ${f.duration.hours}h ${f.duration.minutes}m%0A` +
                                `👥 Passengers: ${bookinginfo?.adults || 1} Adult(s)`;
                            
                            if (bookinginfo?.children && parseInt(bookinginfo.children) > 0) {
                                message += `, ${bookinginfo.children} Child(ren)`;
                            }
                            if (bookinginfo?.infants && parseInt(bookinginfo.infants) > 0) {
                                message += `, ${bookinginfo.infants} Infant(s)`;
                            }
                            
                            // Get price for WhatsApp message (GBP)
                            (async () => {
                                try {
                                    const gbpAmount = await convertToGBP(f.amount, f.currency === 'USD' ? 'USD' : 'GBP');
                                    const gbpFormatted = formatCurrency(gbpAmount, 'GBP');
                                    message += `%0A💰 Total Price: ${gbpFormatted}%0A` +
                                        `━━━━━━━━━━━━━━━━%0A%0A` +
                                        `Please confirm availability and booking process. Thank you!`;
                                    window.open(`https://api.whatsapp.com/send/?phone=442081949506&text=${message}`, '_blank');
                                } catch (error) {
                                    console.error('Error converting currency:', error);
                                    message += `%0A💰 Total Price: £...%0A` +
                                        `━━━━━━━━━━━━━━━━%0A%0A` +
                                        `Please confirm availability and booking process. Thank you!`;
                                    window.open(`https://api.whatsapp.com/send/?phone=442081949506&text=${message}`, '_blank');
                                }
                            })();
                        }}>
                            <img src={whatsapp} className="h-[28px] w-[28px]" alt="WhatsApp" />
                            <p className="text-md md:text-lg font-semibold tracking-[1px] text-green-600">WhatsApp Us</p>
                        </div>
                    </div>
                </div>

            </div>)
        }) : <p className="text-lg text-mLightGray">No Flights</p>
    )
}

const AccordionItem = ({ title, content, index, activeIndex, setActiveIndex, flight, showReturn, bookinginfo, flightAlternatives = {} }) => {

    const [showMobileModal, setShowMobileModal] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const originSegments = flight?.origin_segments || [];
    const returnSegments = flight?.return_segments || flight?.return_origin_segments || [];
    const isRoundTrip = !!bookinginfo?.return_date;
    const sections = isRoundTrip
        ? [
            { label: 'Outbound', segments: originSegments },
            { label: 'Return', segments: returnSegments }
        ]
        : [{ label: null, segments: originSegments }];

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => {
            window.removeEventListener('resize', checkMobile);
            // Cleanup: restore body overflow if modal was open
            if (showMobileModal) {
                document.body.style.overflow = '';
            }
        };
    }, [showMobileModal]);

    // Helper function to format date - ensures correct date display regardless of timezone
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            // Extract date part from ISO string to avoid timezone conversion
            // Handle both "2025-12-16" and "2025-12-16T19:00:00.000Z" formats
            let datePart = dateString;
            if (dateString.includes('T')) {
                datePart = dateString.split('T')[0];
            }
            
            // Remove any timezone info if present
            datePart = datePart.trim();
            
            // Parse the date part (YYYY-MM-DD format)
            const parts = datePart.split('-');
            if (parts.length !== 3) {
                throw new Error('Invalid date format');
            }
            
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            
            // Validate the date values
            if (isNaN(year) || isNaN(month) || isNaN(day)) {
                throw new Error('Invalid date values');
            }
            
            // Create date using UTC at noon to avoid day boundary issues
            const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            
            // Format using UTC timezone to ensure correct date display
            return utcDate.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric',
                timeZone: 'UTC'
            });
        } catch (error) {
            // Fallback: try to parse as-is and format using UTC
            try {
        const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return 'N/A'; // Return N/A if invalid
                }
                // Extract date components using UTC to avoid timezone issues
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth();
                const day = date.getUTCDate();
                const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
                return utcDate.toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric',
                    timeZone: 'UTC'
                });
            } catch (e) {
                return 'N/A'; // Return N/A if all parsing fails
            }
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const calculateLayover = (arrivalTime, nextDepartureTime) => {
        if (!arrivalTime || !nextDepartureTime) return null;
        
        try {
            const arrival = new Date(arrivalTime);
            const departure = new Date(nextDepartureTime);
            
            // Check if dates are valid
            if (isNaN(arrival.getTime()) || isNaN(departure.getTime())) {
                return null;
            }
            
            const diffMs = departure - arrival;
            
            // Check if departure is actually after arrival (positive difference)
            if (diffMs < 0) {
                return null;
            }
            
            const diffMins = Math.floor(diffMs / (1000 * 60));
            const hours = Math.floor(diffMins / 60);
            const minutes = diffMins % 60;
            
            if (hours > 0) {
                return `${hours}h ${minutes}m`;
            }
            return `${minutes}m`;
        } catch (error) {
            console.error('Error calculating layover:', error);
            return null;
        }
    };

    const isActive = activeIndex === index;

    const handleClick = () => {
        if (isMobile) {
            setShowMobileModal(true);
            document.body.style.overflow = 'hidden'; // Prevent background scrolling
        } else {
            setActiveIndex(isActive ? null : index);
        }
    };

    const handleCloseModal = () => {
        setShowMobileModal(false);
        document.body.style.overflow = ''; // Restore scrolling
    };

    return (
        <>
        <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
            {/* Header - Clickable */}
            <div
                role="button"
                onClick={handleClick}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-navy-50 to-navy-100 hover:from-navy-100 hover:to-navy-200 transition-all duration-200 cursor-pointer"
            >
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{title}</p>
                    </div>
                    <FlightPriceGbp
                        amount={flight.amount}
                        sourceCurrency={flight.currency || 'GBP'}
                        className="text-2xl font-bold text-navy-700"
                    />
                    {bookinginfo && (
                        <div className="mt-1 text-xs text-gray-500">
                            {parseInt(bookinginfo.adults || '1') > 0 && (
                                <span>{parseInt(bookinginfo.adults || '1')}x Adult{parseInt(bookinginfo.adults || '1') > 1 ? 's' : ''}</span>
                            )}
                            {parseInt(bookinginfo.children || '0') > 0 && (
                                <span>{parseInt(bookinginfo.adults || '1') > 0 ? ', ' : ''}{parseInt(bookinginfo.children || '0')}x Child{parseInt(bookinginfo.children || '0') > 1 ? 'ren' : ''}</span>
                            )}
                            {parseInt(bookinginfo.infants || '0') > 0 && (
                                <span>{(parseInt(bookinginfo.adults || '1') > 0 || parseInt(bookinginfo.children || '0') > 0) ? ', ' : ''}{parseInt(bookinginfo.infants || '0')}x Infant{parseInt(bookinginfo.infants || '0') > 1 ? 's' : ''}</span>
                            )}
                        </div>
                    )}
                    {(flight.baggage_checked || flight.baggage_cabin) && (
                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-600">
                            {flight.baggage_checked && (
                                <span className="flex items-center gap-1.5" title="Checked baggage">
                                    <Luggage className="w-4 h-4 text-secondary flex-shrink-0" />
                                    <span>{flight.baggage_checked}</span>
                                </span>
                            )}
                            {flight.baggage_cabin && (
                                <span className="flex items-center gap-1.5" title="Cabin baggage">
                                    <Briefcase className="w-4 h-4 text-secondary flex-shrink-0" />
                                    <span>{flight.baggage_cabin}</span>
                                </span>
                            )}
                        </div>
                    )}
                    
                    {/* Alternative Fare Options - Temporarily disabled due to scope issues */}
                    {/* TODO: Re-implement this feature in a future update */}
                </div>
                <div className="flex items-center gap-2">
                    {isMobile ? (
                        <ChevronDown className="w-5 h-5 text-secondary" />
                    ) : (
                        isActive ? <ChevronUp className="w-5 h-5 text-secondary" /> : <ChevronDown className="w-5 h-5 text-secondary" />
                    )}
                </div>
            </div>

            {/* Content - Expandable (Desktop only) */}
            <div
                className={`hidden md:block overflow-hidden transition-all duration-300 ease-in-out ${
                    isActive ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
                {isActive && (
                    <div className="p-6 bg-gray-50">
                        {/* Airline Name */}
                        {flight && (flight.airline || (flight.all_airlines && flight.all_airlines.length > 0)) && (
                            <div className="mb-4 pb-3 border-b border-gray-200">
                                <div className="flex items-center gap-2">
                                    <Plane className="w-4 h-4 text-secondary" />
                                    <span className="text-sm font-semibold text-gray-600">Airline:</span>
                                    <span className="text-base font-bold text-gray-800">
                                        {flight.all_airlines && flight.all_airlines.length > 1 
                                            ? flight.all_airlines.map(a => a.code).join(' + ')
                                            : flight.airline}
                                    </span>
                                </div>
                            </div>
                        )}
                        
                        <div className="space-y-4">
                            {sections.map((section, sectionIdx) => (
                                <div key={sectionIdx}>
                                    {section.label && (
                                        <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 mt-4 first:mt-0">{section.label}</h4>
                                    )}
                                    {section.segments.map((seg, segIndex) => {
                                const depTime = seg.departing_at || seg.origin?.datetime || seg.origin?.time || seg.departure?.at || seg.departure?.datetime;
                                const arrTime = seg.arriving_at || seg.destination?.datetime || seg.destination?.time || seg.arrival?.at || seg.arrival?.datetime;
                                
                                const departureTime = formatTime(depTime);
                                const arrivalTime = formatTime(arrTime);
                                const departureDate = formatDate(depTime);
                                const arrivalDate = formatDate(arrTime);
                                const isOvernight = departureDate !== arrivalDate;
                                
                                // Calculate layover time if not the last segment
                                const isLastSegment = segIndex === section.segments.length - 1;
                                const currentArrivalTime = arrTime;
                                const nextSegment = !isLastSegment ? section.segments[segIndex + 1] : null;
                                const nextDepartureTime = nextSegment 
                                    ? (nextSegment.departing_at || nextSegment.origin?.datetime || nextSegment.origin?.time || nextSegment.departure?.at || nextSegment.departure?.datetime)
                                    : null;
                                const layoverTime = !isLastSegment && currentArrivalTime && nextDepartureTime
                                    ? calculateLayover(currentArrivalTime, nextDepartureTime)
                                    : null;
                                
                                return (
                                    <React.Fragment key={`${sectionIdx}-${segIndex}`}>
                                        <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                                        {/* Flight Number */}
                                        {seg.marketing_carrier && seg.marketing_carrier_code && (
                                            <div className="mb-4 pb-3 border-b border-gray-200">
                                                <div className="flex items-center gap-2">
                                                    <Plane className="w-4 h-4 text-secondary" />
                                                    <span className="text-sm font-semibold text-gray-600">Flight:</span>
                                                    <span className="text-base font-bold text-gray-800">
                                                        {seg.marketing_carrier_code}{seg.marketing_carrier_flight_number || seg.flight_number || ''}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Departure Section */}
                                            <div className="flex flex-col">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex flex-col items-center mt-1">
                                                        <div className="rounded-full bg-secondary h-4 w-4 border-2 border-white shadow-md"></div>
                                                        <div className="w-0.5 h-full bg-gradient-to-b from-secondary to-gray-300 my-1"></div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Clock className="w-4 h-4 text-gray-500" />
                                                            <span className="text-2xl font-bold text-gray-800">{departureTime}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 mb-2">{departureDate}</p>
                                                        <div className="flex items-center gap-2">
                                                            <MapPin className="w-4 h-4 text-secondary" />
                                                            <div>
                                                                <p className="font-semibold text-gray-800 text-sm">
                                                                    {seg.origin?.iata_code || seg.origin?.iata || 'N/A'}
                                                                </p>
                                                                <p className="text-xs text-gray-600">{seg.origin?.name || seg.origin?.city_name || 'N/A'}</p>
                                                                {seg.origin?.terminal && (
                                                                    <p className="text-xs text-gray-500 mt-1">Terminal: {seg.origin.terminal}</p>
                                                                )}
                                                                {seg.origin?.gate && (
                                                                    <p className="text-xs text-gray-500">Gate: {seg.origin.gate}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Duration Section - Center */}
                                            <div className="flex flex-col items-center justify-center py-2">
                                                <div className="bg-navy-100 rounded-lg px-4 py-2 mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4 text-secondary" />
                                                        <span className="text-sm font-bold text-navy-700">
                                                            {seg.duration || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isOvernight && (
                                                    <span className="text-xs text-orange-600 font-semibold bg-orange-50 px-2 py-1 rounded">
                                                        +1 Day
                                                    </span>
                                                )}
                                                {seg.stops && seg.stops > 0 && (
                                                    <span className="text-xs text-gray-600 mt-1">
                                                        {seg.stops} {seg.stops === 1 ? 'stop' : 'stops'}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Arrival Section */}
                                            <div className="flex flex-col">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex flex-col items-center mt-1">
                                                        <div className="w-0.5 h-full bg-gradient-to-b from-gray-300 to-green-600 my-1"></div>
                                                        <div className="rounded-full bg-green-600 h-4 w-4 border-2 border-white shadow-md"></div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Clock className="w-4 h-4 text-gray-500" />
                                                            <span className="text-2xl font-bold text-gray-800">{arrivalTime}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 mb-2">{arrivalDate}</p>
                                                        <div className="flex items-center gap-2">
                                                            <MapPin className="w-4 h-4 text-green-600" />
                                                            <div>
                                                                <p className="font-semibold text-gray-800 text-sm">
                                                                    {seg.destination?.iata_code || seg.destination?.iata || 'N/A'}
                                                                </p>
                                                                <p className="text-xs text-gray-600">{seg.destination?.name || seg.destination?.city_name || 'N/A'}</p>
                                                                {seg.destination?.terminal && (
                                                                    <p className="text-xs text-gray-500 mt-1">Terminal: {seg.destination.terminal}</p>
                                                                )}
                                                                {seg.destination?.gate && (
                                                                    <p className="text-xs text-gray-500">Gate: {seg.destination.gate}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Additional Info */}
                                        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {seg.aircraft && (
                                                <div className="flex items-center gap-2">
                                                    <Plane className="w-4 h-4 text-gray-500" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Aircraft</p>
                                                        <p className="text-sm font-semibold text-gray-700">{seg.aircraft.name || seg.aircraft || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            )}
                                            {seg.cabin_class && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500">Cabin Class:</span>
                                                    <span className="text-sm font-semibold text-gray-700 capitalize">{seg.cabin_class}</span>
                                                </div>
                                            )}
                                        </div>
                                        </div>

                                        {/* Layover Information - Show after segment if not last */}
                                        {!isLastSegment && layoverTime && (
                                            <div className="flex items-center justify-center my-3">
                                                <div className="flex items-center gap-3 bg-orange-50 rounded-lg px-4 py-3 border border-orange-200 shadow-sm max-w-md w-full">
                                                    <div className="flex-shrink-0">
                                                        <Clock className="w-5 h-5 text-orange-600" />
                                                    </div>
                                                    <div className="flex-1 text-center">
                                                        <p className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">Layover at</p>
                                                        <p className="text-base font-bold text-orange-700 mb-1">
                                                            {seg.destination?.name || seg.destination?.city_name || seg.destination?.iata_code || 'N/A'}
                                                        </p>
                                                        <div className="flex items-center justify-center gap-3 text-xs text-orange-600">
                                                            <span className="font-semibold">{seg.destination?.iata_code || seg.destination?.iata || ''}</span>
                                                            {seg.destination?.terminal && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>Terminal {seg.destination.terminal}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0 bg-orange-200 rounded-lg px-3 py-2">
                                                        <p className="text-sm font-bold text-orange-700">{layoverTime}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                                </div>
                            ))}

                            {/* Baggage Info */}
                            {(flight.baggage_checked || flight.baggage_cabin) && (
                                <div className="flex items-center gap-4 text-sm text-gray-700 bg-navy-50 rounded-lg p-4 border border-navy-200">
                                    {flight.baggage_checked && (
                                        <span className="flex items-center gap-2" title="Checked baggage">
                                            <Luggage className="w-5 h-5 text-secondary flex-shrink-0" />
                                            <span>{flight.baggage_checked}</span>
                                        </span>
                                    )}
                                    {flight.baggage_cabin && (
                                        <span className="flex items-center gap-2" title="Cabin baggage">
                                            <Briefcase className="w-5 h-5 text-secondary flex-shrink-0" />
                                            <span>{flight.baggage_cabin}</span>
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-500 italic">Per passenger</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Mobile Overlay Modal */}
        {showMobileModal && (
            <div className="fixed inset-0 z-[9999] md:hidden">
                {/* Backdrop */}
                <div 
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={handleCloseModal}
                ></div>
                
                {/* Bottom Sheet */}
                <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
                    {/* Header with Close Button */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-navy-50 to-navy-100">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{title}</p>
                            </div>
                            <FlightPriceGbp
                                amount={flight.amount}
                                sourceCurrency={flight.currency || 'GBP'}
                                className="text-2xl font-bold text-navy-700"
                            />
                        </div>
                        <button
                            onClick={handleCloseModal}
                            className="ml-4 p-2 rounded-full hover:bg-white/50 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                        <div className="space-y-4">
                            {sections.map((section, sectionIdx) => (
                                <div key={sectionIdx}>
                                    {section.label && (
                                        <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 mt-4 first:mt-0">{section.label}</h4>
                                    )}
                                    {section.segments.map((seg, segIndex) => {
                                const depTime = seg.departing_at || seg.origin?.datetime || seg.origin?.time || seg.departure?.at || seg.departure?.datetime;
                                const arrTime = seg.arriving_at || seg.destination?.datetime || seg.destination?.time || seg.arrival?.at || seg.arrival?.datetime;
                                
                                const departureTime = formatTime(depTime);
                                const arrivalTime = formatTime(arrTime);
                                const departureDate = formatDate(depTime);
                                const arrivalDate = formatDate(arrTime);
                                const isOvernight = departureDate !== arrivalDate;
                                
                                // Calculate layover time if not the last segment
                                const isLastSegment = segIndex === section.segments.length - 1;
                                const currentArrivalTime = arrTime;
                                const nextSegment = !isLastSegment ? section.segments[segIndex + 1] : null;
                                const nextDepartureTime = nextSegment 
                                    ? (nextSegment.departing_at || nextSegment.origin?.datetime || nextSegment.origin?.time || nextSegment.departure?.datetime)
                                    : null;
                                const layoverTime = !isLastSegment && currentArrivalTime && nextDepartureTime
                                    ? calculateLayover(currentArrivalTime, nextDepartureTime)
                                    : null;
                                
                                return (
                                    <React.Fragment key={segIndex}>
                                        <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                                            {/* Airline Name */}
                                            {flight && (flight.airline || (flight.all_airlines && flight.all_airlines.length > 0)) && (
                                                <div className="mb-3 pb-3 border-b border-gray-200">
                                                    <div className="flex items-center gap-2">
                                                        <Plane className="w-4 h-4 text-secondary" />
                                                        <span className="text-sm font-semibold text-gray-600">Airline:</span>
                                                        <span className="text-base font-bold text-gray-800">
                                                            {flight.all_airlines && flight.all_airlines.length > 1 
                                                                ? flight.all_airlines.map(a => a.code).join(' + ')
                                                                : flight.airline}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Flight Number */}
                                            {seg.marketing_carrier && seg.marketing_carrier_code && (
                                                <div className="mb-4 pb-3 border-b border-gray-200">
                                                    <div className="flex items-center gap-2">
                                                        <Plane className="w-4 h-4 text-secondary" />
                                                        <span className="text-sm font-semibold text-gray-600">Flight:</span>
                                                        <span className="text-base font-bold text-gray-800">
                                                            {seg.marketing_carrier_code}{seg.marketing_carrier_flight_number || seg.flight_number || ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                {/* Departure Section */}
                                                <div className="flex flex-col">
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex flex-col items-center mt-1">
                                                            <div className="rounded-full bg-secondary h-4 w-4 border-2 border-white shadow-md"></div>
                                                            <div className="w-0.5 h-full bg-gradient-to-b from-secondary to-gray-300 my-1"></div>
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Clock className="w-4 h-4 text-gray-500" />
                                                                <span className="text-2xl font-bold text-gray-800">{departureTime}</span>
                                                            </div>
                                                            <p className="text-xs text-gray-500 mb-2">{departureDate}</p>
                                                            <div className="flex items-center gap-2">
                                                                <MapPin className="w-4 h-4 text-secondary" />
                                                                <div>
                                                                    <p className="font-semibold text-gray-800 text-sm">
                                                                        {seg.origin?.iata_code || seg.origin?.iata || 'N/A'}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">{seg.origin?.name || seg.origin?.city_name || 'N/A'}</p>
                                                                    {seg.origin?.terminal && (
                                                                        <p className="text-xs text-gray-500 mt-1">Terminal: {seg.origin.terminal}</p>
                                                                    )}
                                                                    {seg.origin?.gate && (
                                                                        <p className="text-xs text-gray-500">Gate: {seg.origin.gate}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Duration Section - Center */}
                                                <div className="flex flex-col items-center justify-center py-2">
                                                    <div className="bg-navy-100 rounded-lg px-4 py-2 mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="w-4 h-4 text-secondary" />
                                                            <span className="text-sm font-bold text-navy-700">
                                                                {seg.duration || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isOvernight && (
                                                        <span className="text-xs text-orange-600 font-semibold bg-orange-50 px-2 py-1 rounded">
                                                            +1 Day
                                                        </span>
                                                    )}
                                                    {seg.stops && seg.stops > 0 && (
                                                        <span className="text-xs text-gray-600 mt-1">
                                                            {seg.stops} {seg.stops === 1 ? 'stop' : 'stops'}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Arrival Section */}
                                                <div className="flex flex-col">
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex flex-col items-center mt-1">
                                                            <div className="w-0.5 h-full bg-gradient-to-b from-gray-300 to-green-600 my-1"></div>
                                                            <div className="rounded-full bg-green-600 h-4 w-4 border-2 border-white shadow-md"></div>
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Clock className="w-4 h-4 text-gray-500" />
                                                                <span className="text-2xl font-bold text-gray-800">{arrivalTime}</span>
                                                            </div>
                                                            <p className="text-xs text-gray-500 mb-2">{arrivalDate}</p>
                                                            <div className="flex items-center gap-2">
                                                                <MapPin className="w-4 h-4 text-green-600" />
                                                                <div>
                                                                    <p className="font-semibold text-gray-800 text-sm">
                                                                        {seg.destination?.iata_code || seg.destination?.iata || 'N/A'}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">{seg.destination?.name || seg.destination?.city_name || 'N/A'}</p>
                                                                    {seg.destination?.terminal && (
                                                                        <p className="text-xs text-gray-500 mt-1">Terminal: {seg.destination.terminal}</p>
                                                                    )}
                                                                    {seg.destination?.gate && (
                                                                        <p className="text-xs text-gray-500">Gate: {seg.destination.gate}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Additional Info */}
                                            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {seg.aircraft && (
                                                    <div className="flex items-center gap-2">
                                                        <Plane className="w-4 h-4 text-gray-500" />
                                                        <div>
                                                            <p className="text-xs text-gray-500">Aircraft</p>
                                                            <p className="text-sm font-semibold text-gray-700">{seg.aircraft.name || seg.aircraft || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {seg.cabin_class && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-500">Cabin Class:</span>
                                                        <span className="text-sm font-semibold text-gray-700 capitalize">{seg.cabin_class}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Layover Information - Show after segment if not last */}
                                        {!isLastSegment && layoverTime && (
                                            <div className="flex items-center justify-center my-4">
                                                <div className="flex items-center gap-3 bg-orange-50 rounded-lg px-4 py-3 border border-orange-200 shadow-sm max-w-md w-full">
                                                    <div className="flex-shrink-0">
                                                        <Clock className="w-5 h-5 text-orange-600" />
                                                    </div>
                                                    <div className="flex-1 text-center">
                                                        <p className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">Layover at</p>
                                                        <p className="text-base font-bold text-orange-700 mb-1">
                                                            {seg.destination?.name || seg.destination?.city_name || seg.destination?.iata_code || 'N/A'}
                                                        </p>
                                                        <div className="flex items-center justify-center gap-3 text-xs text-orange-600">
                                                            <span className="font-semibold">{seg.destination?.iata_code || seg.destination?.iata || ''}</span>
                                                            {seg.destination?.terminal && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>Terminal {seg.destination.terminal}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0 bg-orange-200 rounded-lg px-3 py-2">
                                                        <p className="text-sm font-bold text-orange-700">{layoverTime}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                                </div>
                            ))}

                            {/* Baggage Info */}
                            {(flight.baggage_checked || flight.baggage_cabin) && (
                                <div className="flex items-center gap-4 text-sm text-gray-700 bg-navy-50 rounded-lg p-4 border border-navy-200">
                                    {flight.baggage_checked && (
                                        <span className="flex items-center gap-2" title="Checked baggage">
                                            <Luggage className="w-5 h-5 text-secondary flex-shrink-0" />
                                            <span>{flight.baggage_checked}</span>
                                        </span>
                                    )}
                                    {flight.baggage_cabin && (
                                        <span className="flex items-center gap-2" title="Cabin baggage">
                                            <Briefcase className="w-5 h-5 text-secondary flex-shrink-0" />
                                            <span>{flight.baggage_cabin}</span>
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-500 italic">Per passenger</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};


export default FlightDetailNewDuffel;




{/* <div className="flex md:justify-end items-center mt-2 md:mt-0">

{bookinginfo?.return_date && selectedSlice.length == 0 ? <button className="btn rounded-xl bg-black text-white hover:bg-primary" onClick={() => {
    if (!selectedSlice.length) {
        let ss = [];
        ss.push(f)
        setSelectedSlice(ss);
    }
}}>Select</button> : <button className="btn rounded-xl bg-primary " onClick={() => {
    let ss = JSON.parse(JSON.stringify(selectedSlice));
    ss.push(f)
    setSelectedSlice(ss);
    // Load name and phone from localStorage if available
    const bookingData = localStorage.getItem('booking');
    if (bookingData) {
        try {
            const booking = JSON.parse(bookingData);
            if (booking.name) {
                setName(booking.name);
            }
            if (booking.phone) {
                setPhone(booking.phone);
            }
        } catch (error) {
            console.error('Error loading booking data:', error);
        }
    }
    document.getElementById('my_modal_flightdetail').showModal()
}}>Book now</button>}
</div> */}